import axios from 'axios';
import { downloadAxiosBlobResponse } from '@/utils/downloadBlob';

// API 기본 URL
const API_BASE_URL = '/api';

/** authToken 순환참조 방지용 — JWT exp만 로컬 판별 */
const readJwtExpMs = (token: string | null | undefined): number | null => {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const exp = Number(JSON.parse(json)?.exp);
    if (!Number.isFinite(exp) || exp <= 0) return null;
    return exp * 1000;
  } catch {
    return null;
  }
};
const isAccessTokenExpired = (token: string | null | undefined) => {
  const exp = readJwtExpMs(token);
  return exp == null || exp <= Date.now();
};
const isAccessTokenFresh = (token: string | null | undefined, skewMs = 10 * 60 * 1000) => {
  const exp = readJwtExpMs(token);
  return exp != null && exp - Date.now() > skewMs;
};

// 도메인별 저장소 키 접두사 (test.caion.kr와 caion.kr 분리)
const getDomainPrefix = (): string => {
  const host = window.location.hostname;
  if (host.includes('test.caion')) return 'test_';
  return '';
};

// 토큰 스토리지 키 (도메인별 분리)
const getTokenKey = (): string => `${getDomainPrefix()}caion_token`;

const getRefreshKey = (): string => `${getDomainPrefix()}caion_refresh`;

// 토큰 관리 함수
export const tokenManager = {
  getToken: (): string | null => {
    const TOKEN_KEY = getTokenKey();
    return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
  },
  
  setToken: (token: string, persistent: boolean = false): void => {
    const TOKEN_KEY = getTokenKey();
    if (persistent) {
      localStorage.setItem(TOKEN_KEY, token);
      sessionStorage.removeItem(TOKEN_KEY);
    } else {
      sessionStorage.setItem(TOKEN_KEY, token);
      localStorage.removeItem(TOKEN_KEY);
    }
  },
  
  removeToken: (): void => {
    const TOKEN_KEY = getTokenKey();
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(getRefreshKey());
  },

  setRefreshToken: (token: string): void => {
    localStorage.setItem(getRefreshKey(), token);
  },

  removeRefreshToken: (): void => {
    localStorage.removeItem(getRefreshKey());
  }
};

const clearClientAuthStorage = (): void => {
  tokenManager.removeToken();
  tokenManager.removeRefreshToken();
  const prefix = getDomainPrefix();
  localStorage.removeItem(`${prefix}caion_auto_login`);
  sessionStorage.removeItem(`${prefix}caion_auth`);
  sessionStorage.removeItem(`${prefix}caion_original_user`);
};

// 갱신 전용 — 인터셉터 루프 방지
const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

export type RefreshSessionResult = {
  token: string;
  refreshToken?: string | null;
  user?: unknown;
};

let refreshPromise: Promise<string | null> | null = null;
let lastRefreshSession: RefreshSessionResult | null = null;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * refresh 1회 사용 후 재발급.
 * 멀티탭에서 다른 탭이 먼저 회전했다면 새 localStorage 토큰을 재사용한다.
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  const session = await refreshAccessSession();
  return session?.token ?? null;
};

export const refreshAccessSession = async (): Promise<RefreshSessionResult | null> => {
  const refreshToken = tokenManager.getRefreshToken();
  if (!refreshToken) return null;

  if (refreshPromise) {
    const token = await refreshPromise;
    if (!token) return null;
    if (lastRefreshSession?.token === token) return lastRefreshSession;
    return { token };
  }

  refreshPromise = (async () => {
    try {
      const res = await refreshClient.post('/auth/refresh', { refreshToken });
      if (!res.data?.success || !res.data.token) return null;

      tokenManager.setToken(res.data.token, true);
      if (res.data.refreshToken) {
        tokenManager.setRefreshToken(res.data.refreshToken);
      }
      lastRefreshSession = {
        token: res.data.token as string,
        refreshToken: res.data.refreshToken || null,
        user: res.data.data,
      };
      return res.data.token as string;
    } catch (error) {
      // 다른 탭이 먼저 회전했을 수 있음
      await sleep(150);
      const latestRefresh = tokenManager.getRefreshToken();
      const latestAccess = tokenManager.getToken();
      if (
        latestRefresh &&
        latestRefresh !== refreshToken &&
        latestAccess &&
        !isAccessTokenExpired(latestAccess)
      ) {
        lastRefreshSession = { token: latestAccess, refreshToken: latestRefresh };
        return latestAccess;
      }
      if (latestRefresh && latestRefresh !== refreshToken) {
        try {
          const res2 = await refreshClient.post('/auth/refresh', {
            refreshToken: latestRefresh,
          });
          if (res2.data?.success && res2.data.token) {
            tokenManager.setToken(res2.data.token, true);
            if (res2.data.refreshToken) {
              tokenManager.setRefreshToken(res2.data.refreshToken);
            }
            lastRefreshSession = {
              token: res2.data.token as string,
              refreshToken: res2.data.refreshToken || null,
              user: res2.data.data,
            };
            return res2.data.token as string;
          }
        } catch {
          /* fall through */
        }
      }
      throw error;
    }
  })().finally(() => {
    refreshPromise = null;
  });

  const token = await refreshPromise;
  if (!token) return null;
  if (lastRefreshSession?.token === token) return lastRefreshSession;
  return { token };
};

/** 만료 임박/만료일 때만 refresh. 유효하면 현재 access 유지 */
export const ensureFreshAccessToken = async (): Promise<string | null> => {
  const access = tokenManager.getToken();
  if (access && isAccessTokenFresh(access)) {
    return access;
  }
  if (!tokenManager.getRefreshToken()) {
    return access && !isAccessTokenExpired(access) ? access : null;
  }
  try {
    return await refreshAccessToken();
  } catch {
    return access && !isAccessTokenExpired(access) ? access : null;
  }
};

// Axios 인스턴스
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 요청 인터셉터 - JWT 토큰 자동 추가
api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 인증이 필요 없는 API 경로 목록
const PUBLIC_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/check-id',
  '/auth/roles',
  '/member/biz-gubun',
  '/crawl/sido',
  '/crawl/sigungu',
  '/crawl/biz-gubun'
];

// 401 리다이렉트 중복 방지 플래그
let isRedirectingToLogin = false;

const redirectToLogin = (): void => {
  if (isRedirectingToLogin) return;
  if (window.location.pathname === '/login') return;
  isRedirectingToLogin = true;
  console.warn('세션 만료 - 로그인 페이지로 이동');
  setTimeout(() => {
    window.location.href = '/login';
  }, 100);
};

const expireClientSession = (): void => {
  clearClientAuthStorage();
  redirectToLogin();
};

// 응답 인터셉터
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || '';
    const isPublicPath = PUBLIC_PATHS.some(path => requestUrl.includes(path));

    const failedConfig = error.config as (typeof error.config & { _retry?: boolean }) | undefined;
    if (status === 401 && !isPublicPath && failedConfig && !failedConfig._retry) {
      const hasRefresh = !!tokenManager.getRefreshToken();
      if (hasRefresh) {
        failedConfig._retry = true;
        try {
          if (!refreshPromise) {
            refreshPromise = refreshAccessToken().finally(() => {
              refreshPromise = null;
            });
          }
          const newToken = await refreshPromise;
          if (newToken) {
            failedConfig.headers = failedConfig.headers || {};
            failedConfig.headers.Authorization = `Bearer ${newToken}`;
            return api.request(failedConfig);
          }
        } catch {
          // 다른 탭이 이미 새 세션을 썼으면 로그아웃하지 않음
          const recovered = tokenManager.getToken();
          if (recovered && !isAccessTokenExpired(recovered)) {
            failedConfig.headers = failedConfig.headers || {};
            failedConfig.headers.Authorization = `Bearer ${recovered}`;
            return api.request(failedConfig);
          }
          expireClientSession();
          return Promise.reject(error);
        }
      }
      expireClientSession();
      return Promise.reject(error);
    }

    if (status !== 401) {
      console.error('API 오류:', error);
    }
    return Promise.reject(error);
  }
);

// ========================================
// 인증 API
// ========================================
export const authApi = {
  // 통합 로그인 (멤버/유저)
  login: (
    loginId: string,
    loginPw: string,
    loginType: string = 'auto',
    memberCode?: number,
    autoLogin: boolean = false
  ) => {
    return api.post('/auth/login', { loginId, loginPw, loginType, memberCode, autoLogin });
  },

  refresh: (refreshToken: string) => {
    return refreshClient.post('/auth/refresh', { refreshToken });
  },

  logout: (refreshToken?: string | null) => {
    return refreshClient.post('/auth/logout', { refreshToken: refreshToken || '' });
  },
  
  // ID 중복 체크
  checkIdDuplicate: (loginId: string, checkType: string = 'all') => {
    return api.get('/auth/check-id', { params: { loginId, checkType } });
  },
  
  // 권한 목록 조회
  getRoles: () => {
    return api.get('/auth/roles');
  },
  
  // 로그인 기록 조회
  getLoginLogs: (params?: {
    type?: string;
    refIdx?: number;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }) => {
    return api.get('/auth/login-logs', { params });
  },
  
  // 특정 유저/멤버의 최근 로그인 기록
  getRecentLogins: (type: string, refIdx: number, limit: number = 10) => {
    return api.get(`/auth/login-logs/${type}/${refIdx}`, { params: { limit } });
  },
  
  // 로그인 통계
  getLoginStats: (days: number = 7) => {
    return api.get('/auth/login-stats', { params: { days } });
  },
  
  // 멤버의 사업구분 목록 조회 (같은 ID를 가진 멤버 목록)
  getMemberBizGubunList: (memberId: string) => {
    return api.get(`/auth/member-biz-gubun/${memberId}`);
  },
  
  // 사업구분 전환
  switchBizGubun: (
    currentMemberIdx: number,
    targetMemberIdx: number,
    refreshToken?: string | null,
    autoLogin: boolean = false
  ) => {
    return api.post('/auth/switch-biz-gubun', {
      currentMemberIdx,
      targetMemberIdx,
      refreshToken: refreshToken || undefined,
      autoLogin
    });
  }
};

// ========================================
// 멤버(기관) API
// ========================================
export const memberApi = {
  // 멤버 목록 조회
  getList: (params?: {
    search?: string;
    bizGubun?: string;
    status?: number | string;
    orgCode?: string;
    roleLevel?: number;
    userIdx?: number;
    page?: number;
    limit?: number;
    serviceScope?: 'service' | 'none';
    sort?: 'recent';
  }) => {
    return api.get('/member', { params });
  },
  
  // 멤버 상세 조회 (권한 체크 포함) — access: { hasWage, hasAccount, isAdmin }
  getById: (
    memberCode: string | number,
    params?: { orgCode?: string; roleLevel?: number; userIdx?: number }
  ) => {
    return api.get(`/member/${memberCode}`, { params });
  },

  /** 시설 임금/회계 담당자 조회 */
  getAssignees: (memberCode: string | number) => {
    return api.get(`/member/${memberCode}/assignees`);
  },

  /** 시설 임금/회계 담당자 저장 (관리자 90+) */
  updateAssignees: (
    memberCode: string | number,
    data: { wage?: number[]; account?: number[]; wage_assignee_ids?: number[]; account_assignee_ids?: number[] }
  ) => {
    return api.put(`/member/${memberCode}/assignees`, data);
  },
  
  // 사업구분 목록 조회
  getBizGubunList: () => {
    return api.get('/member/biz-gubun');
  },
  
  // 멤버 생성
  create: (data: any) => {
    return api.post('/member', data);
  },
  
  // 멤버 수정
  update: (memberCode: number | string, data: any) => {
    return api.put(`/member/${memberCode}`, data);
  },
  
  // 멤버 삭제
  delete: (memberCode: number | string, reason?: string) => {
    return api.delete(`/member/${memberCode}`, { data: { reason } });
  },
  
  // 크롤링 데이터 검색 (회원가입용)
  searchCrawlData: (search: string) => {
    return api.get('/member/crawl-search', { params: { search } });
  },
  
  // 크롤링 데이터로 멤버 정보 가져오기
  getCrawlDataForMember: (ccNumber: string) => {
    return api.get(`/member/crawl-data/${ccNumber}`);
  }
};

// ========================================
// 사용자 API
// ========================================
export const userApi = {
  // 사용자 목록 조회
  getList: (params?: {
    search?: string;
    roleCode?: string;
    status?: number | string;
    orgCode?: string;
    roleLevel?: number;
    page?: number;
    limit?: number;
  }) => {
    return api.get('/user', { params });
  },
  
  // 담당자 목록 조회 (멤버 담당자 지정용)
  getManagers: (params?: {
    orgCode?: string;
    roleLevel?: number;
  }) => {
    return api.get('/user/managers', { params });
  },
  
  // 사용자 상세 조회
  getById: (userIdx: number) => {
    return api.get(`/user/${userIdx}`);
  },
  
  // 사용자 생성
  create: (data: any) => {
    return api.post('/user', data);
  },
  
  // 사용자 수정
  update: (userIdx: number, data: any, requesterRoleLevel?: number) => {
    const params = requesterRoleLevel ? `?requesterRoleLevel=${requesterRoleLevel}` : '';
    return api.put(`/user/${userIdx}${params}`, data);
  },
  
  // 비밀번호 변경
  changePassword: (userIdx: number, oldPw: string, newPw: string) => {
    return api.put(`/user/${userIdx}/password`, { oldPw, newPw });
  },
  
  // 사용자 삭제
  delete: (userIdx: number, requesterRoleLevel?: number) => {
    const params = requesterRoleLevel ? `?requesterRoleLevel=${requesterRoleLevel}` : '';
    return api.delete(`/user/${userIdx}${params}`);
  }
};

// ========================================
// 조직 API
// ========================================
export const orgApi = {
  // 조직 목록 조회
  getList: (params?: {
    level?: string;
    parentCode?: string;
    status?: number | string;
    search?: string;
  }) => {
    return api.get('/org', { params });
  },
  
  // 조직 트리 조회
  getTree: () => {
    return api.get('/org/tree');
  },
  
  // 조직 통계 조회
  getStats: (params?: {
    orgCode?: string;
    roleLevel?: number;
  }) => {
    return api.get('/org/stats', { params });
  },
  
  // 조직 상세 조회
  getByCode: (orgCode: string) => {
    return api.get(`/org/${orgCode}`);
  },
  
  // 조직 생성
  create: (data: any) => {
    return api.post('/org', data);
  },
  
  // 조직 수정
  update: (orgCode: string, data: any) => {
    return api.put(`/org/${orgCode}`, data);
  },
  
  // 조직 삭제
  delete: (orgCode: string) => {
    return api.delete(`/org/${orgCode}`);
  }
};

// ========================================
// 크롤링 API
// ========================================
export const crawlApi = {
  // 시도 목록 조회
  getSidoList: () => {
    return api.get('/crawl/sido');
  },
  
  // 시군구 목록 조회
  getSigunguList: (sidoCode?: string) => {
    return api.get('/crawl/sigungu', { params: { sidoCode } });
  },
  
  // 크롤링 시작 (여러 시도 선택 가능)
  startCrawling: (sidoCodes?: string[]) => {
    return api.post('/crawl/start', { sidoCodes }, { timeout: 0 });
  },
  
  // 크롤링 중단
  stopCrawling: () => {
    return api.post('/crawl/stop');
  },
  
  // 크롤링 상태 조회
  getStatus: () => {
    return api.get('/crawl/status');
  },
  
  // 크롤링 통계 조회
  getStats: () => {
    return api.get('/crawl/stats');
  },
  
  // 크롤링 데이터 목록 조회
  getList: (params?: {
    sidoCode?: string | null;
    sigunguCode?: string | null;
    bizGubun?: string | null;
    bizGubuns?: string | null;
    search?: string;
    minCapacity?: number | null;
    minCurrent?: number | null;
    salesFilter?: string | null;
    wageDiagFilter?: string | null;
    page?: number;
    limit?: number;
  }) => {
    return api.get('/crawl/list', { params });
  },

  // 기관 상세 조회
  getDetail: (ccNumber: string) => {
    return api.get(`/crawl/detail/${ccNumber}`);
  },

  // 사업구분 목록 조회
  getBizGubunList: () => {
    return api.get('/crawl/biz-gubun');
  },
  
  // 크롤링 로그 조회
  getLogs: (limit?: number) => {
    return api.get('/crawl/logs', { params: { limit } });
  },
  
  // 크롤링 로그 스트림 URL (SSE)
  getLogsStreamUrl: () => {
    return `${API_BASE_URL}/crawl/logs/stream`;
  },
  
  // 크롤링 데이터 초기화
  clearData: () => {
    return api.delete('/crawl/clear');
  },
  
  // 엑셀 다운로드 컬럼 정의 조회
  getExcelColumns: () => {
    return api.get('/crawl/excel/columns');
  },
  
  // 엑셀 다운로드 미리보기 (건수, columns가 있으면 앞에서 몇 곳)
  previewExcel: (filters: any, columns?: string[]) => {
    return api.post('/crawl/excel/preview', { filters, columns });
  },
  
  // 임금간편진단 요약 (목록 컬럼용)
  getWageDiagnosisSummary: (ccNumbers: string[]) => {
    return api.get('/crawl/wage-diagnosis/summary', {
      params: { ccNumbers: ccNumbers.join(',') },
    });
  },

  getWageDiagnosisActive: (ccNumber: string) => {
    return api.get(`/crawl/wage-diagnosis/active/${encodeURIComponent(ccNumber)}`);
  },

  getWageDiagnosisReport: (ccNumber: string) => {
    return api.get(`/crawl/wage-diagnosis/report/${encodeURIComponent(ccNumber)}`);
  },

  getWageDiagnosisReportData: (ccNumber: string) => {
    return api.get(`/crawl/wage-diagnosis/report-data/${encodeURIComponent(ccNumber)}`);
  },

  getWageDiagnosisReportV2: (ccNumber: string) => {
    return api.get(`/crawl/wage-diagnosis/report-v2/${encodeURIComponent(ccNumber)}`);
  },

  uploadWageDiagnosis: (payload: {
    ccNumber: string;
    name: string;
    biz?: string;
    ym: string;
    nonTaxLunch: boolean;
    rows: any[];
  }) => {
    return api.post('/crawl/wage-diagnosis/upload', payload);
  },

  updateWageDiagnosisSettings: (ccNumber: string, payload: { nonTaxLunch: boolean }) => {
    return api.patch(`/crawl/wage-diagnosis/${encodeURIComponent(ccNumber)}/settings`, payload);
  },

  deleteWageDiagnosis: (ccNumber: string) => {
    return api.delete(`/crawl/wage-diagnosis/${encodeURIComponent(ccNumber)}`);
  },

  downloadWageDiagnosisTemplate: async () => {
    const response = await api.get('/crawl/wage-diagnosis/template', { responseType: 'blob' });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = '임금대장상세진단양식_함우리.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
  },

  // 엑셀 다운로드
  downloadExcel: async (columns: string[], filters: any) => {
    const response = await api.post('/crawl/excel/download', { columns, filters }, {
      responseType: 'blob',
    });

    downloadAxiosBlobResponse(response, '장기요양기관목록.xlsx');

    return response;
  },
};

// ========================================
// 영업 관리 API
// ========================================
export const salesApi = {
  // 시설의 영업 정보 조회 (영업정보 + 로그)
  getSalesByFacility: (ccNumber: string) => {
    return api.get(`/sales/facility/${ccNumber}`);
  },
  
  // 영업 시작
  startSales: (data: { 
    cc_number: string; 
    org_code: string; 
    org_name: string;
    u_idx: number;
    u_name: string;
  }) => {
    return api.post('/sales/start', data);
  },
  
  // 영업 중단
  stopSales: (salesIdx: number, data: {
    stop_reason: string;
    cc_number: string;
    org_code: string;
    org_name: string;
    u_idx: number;
    u_name: string;
  }) => {
    return api.post(`/sales/${salesIdx}/stop`, data);
  },
  
  // 회원가입 (영업 성공)
  convertSales: (salesIdx: number, data: {
    cc_number: string;
    org_code: string;
    org_name: string;
    u_idx: number;
    u_name: string;
    member_id?: string;
    member_pw?: string;
  }) => {
    return api.post(`/sales/${salesIdx}/convert`, data);
  },

  // 영업 정보 수정 (메모)
  updateSales: (salesIdx: number, data: { 
    sales_note?: string;
    org_code?: string;
  }) => {
    return api.put(`/sales/${salesIdx}`, data);
  },
  
  // 영업 로그 수정 (관리자 전용)
  updateSalesLog: (logIdx: number, logContent: string) => {
    return api.put(`/sales/log/${logIdx}`, { log_content: logContent });
  },

  // 영업 로그 삭제 (관리자 전용 — log_status='deleted'로 마킹, 조회에서 제외)
  deleteSalesLog: (logIdx: number) => {
    return api.delete(`/sales/log/${logIdx}`);
  },

  // 숨김 처리 토글 (관리자 전용)
  toggleHidden: (salesIdx: number, data: {
    is_hidden: number;
    cc_number: string;
    org_code: string;
    org_name: string;
    u_idx: number;
    u_name: string;
  }) => {
    return api.post(`/sales/${salesIdx}/toggle-hidden`, data);
  }
};

// ========================================
// 통장내역 API
// ========================================
export const bankbookApi = {
  // 통장내역 목록 조회
  getList: (memberCode: string | number, year: string, month: string, bizGubun?: string, bankCode?: string) => {
    return api.get(`/bankbook/list/${memberCode}`, { params: { year, month, bizGubun, bankCode } });
  },
  
  // 계정코드 목록 조회
  getAccountCodes: (year?: string, gubun?: string) => {
    return api.get('/bankbook/account-codes', { params: { year, gubun } });
  },
  
  // 통장내역 항목 생성
  create: (data: {
    m_code: string | number;
    cb_biz_gubun?: string;
    cb_gubun: string;
    cb_date: string;
    cb_price: number;
    cb_memo: string;
    cb_scode?: string;
    cb_rel_acc?: string;
    cb_bank_name?: string;
    cb_bank_balance?: number;
    cb_bank_code?: string;
    cb_source?: string;
  }) => {
    return api.post('/bankbook', data);
  },
  
  // 통장내역 중간 삽입 (특정 항목의 위/아래에 삽입)
  insertAt: (data: {
    targetCbIdx: number;     // 기준 항목 idx
    position: 'before' | 'after';  // 'before' (위에 삽입) 또는 'after' (아래에 삽입)
    m_code: number;
    cb_biz_gubun?: string;
    cb_gubun: 'IN' | 'OUT';
    cb_date: string;
    cb_price: number;
    cb_memo?: string;
    cb_scode?: string;
    cb_funding?: string;
    cb_rel_acc?: string;
    cb_bank_name?: string;
    cb_bank_balance?: number;
    cb_bank_code?: string;
    cb_source?: string;
  }) => {
    return api.post('/bankbook/insert-at', data);
  },
  
  // 통장내역 항목 수정
  update: (cbIdx: number, data: {
    cb_scode?: string;
    cb_funding?: string;
    cb_memo?: string;
    cb_price?: number;
    cb_gubun?: 'IN' | 'OUT';
    cb_rel_acc?: string;
    cb_matched_beneficiary_idx?: number | null;
  }) => {
    return api.put(`/bankbook/${cbIdx}`, data);
  },
  
  // 통장내역 항목 삭제
  delete: (cbIdx: number) => {
    return api.delete(`/bankbook/${cbIdx}`);
  },
  
  // 통장내역 다중 항목 삭제
  deleteMultiple: (idxList: number[]) => {
    return api.post('/bankbook/delete-multiple', { idxList });
  },
  
  // 통장내역 확정
  confirm: (memberCode: string | number, year: string, month: string, bizGubun?: string) => {
    return api.post(`/bankbook/confirm/${memberCode}`, { year, month, bizGubun });
  },
  
  // 통장내역 확정 해제
  unconfirm: (memberCode: string | number, year: string, month: string, bizGubun?: string) => {
    return api.post(`/bankbook/unconfirm/${memberCode}`, { year, month, bizGubun });
  },
  
  // 엑셀 업로드
  uploadExcel: (memberCode: string | number, formData: FormData) => {
    return api.post(`/bankbook/upload/${memberCode}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000
    });
  },
  
  // 엑셀 파일 분석 (헤더/컬럼 매핑 확인용)
  analyzeExcel: (memberCode: string | number, formData: FormData) => {
    return api.post(`/bankbook/analyze/${memberCode}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000
    });
  },
  
  // 이월금 조회
  getCarryover: (memberCode: string | number, year: string, month: string, bizGubun?: string) => {
    return api.get(`/bankbook/carryover/${memberCode}`, { params: { year, month, bizGubun } });
  },
  
  // 누적 이월금 조회 (stepu 정책: 해당 연도 1월~전월 누적, 단일 쿼리)
  getCumulativeCarryover: (memberCode: string | number, year: string, month: string, bizGubun?: string, bankCode?: string) => {
    return api.get(`/bankbook/cumulative-carryover/${memberCode}`, { params: { year, month, bizGubun, bankCode } });
  },
  
  // 이월금 설정
  setCarryover: (memberCode: string | number, data: {
    year: string;
    month: string;
    balance: number;
    bizGubun?: string;
  }) => {
    return api.post(`/bankbook/carryover/${memberCode}`, data);
  },

  // 이달의 메모 (기관·연월)
  getMonthMemo: (memberCode: string | number, year: string, month: string) => {
    return api.get(`/bankbook/month-memo/${memberCode}`, { params: { year, month } });
  },
  saveMonthMemo: (memberCode: string | number, data: {
    year: string;
    month: string;
    memo: string;
  }) => {
    return api.put(`/bankbook/month-memo/${memberCode}`, data);
  },

  /** 계정 추천 설정 (기관) */
  getAccountRecommendSetting: (memberCode: string | number) => {
    return api.get(`/bankbook/account-recommend-setting/${memberCode}`);
  },
  saveAccountRecommendSetting: (memberCode: string | number, data: {
    enabled: boolean;
    scope: 'facility' | 'bizGubun' | 'all';
  }) => {
    return api.put(`/bankbook/account-recommend-setting/${memberCode}`, data);
  },

  /** 자금원천 「기준대로 넣기」 규칙 (기관) */
  getFundingInferSetting: (memberCode: string | number) => {
    return api.get(`/bankbook/funding-infer-setting/${memberCode}`);
  },
  saveFundingInferSetting: (memberCode: string | number, data: {
    enabled: boolean;
    useRelAccountFunding: boolean;
    expenseNameRules: Array<{ name: string; funding: string }>;
    incomeFunding: Record<string, string>;
  }) => {
    return api.put(`/bankbook/funding-infer-setting/${memberCode}`, data);
  },

  /** 연도·사업구분 기준 인건비 충족비율(%) · memberCode+month 시 1월~해당월 누적 실적 포함 */
  getLaborBaseRatio: (
    year: string,
    bizGubun?: string,
    opts?: { memberCode?: string; month?: string | number; bankCode?: string | null }
  ) => {
    return api.get('/bankbook/labor-base-ratio', {
      params: {
        year,
        bizGubun,
        memberCode: opts?.memberCode,
        month: opts?.month,
        bankCode: opts?.bankCode || undefined,
      },
    });
  },
  
  // ========================================
  // 적요 학습 관련 API
  // ========================================
  
  // 적요 그룹화 (엑셀 업로드 후 학습 전 단계)
  groupMemos: (memberCode: string | number, data: {
    memos: Array<{ memo: string; gubun: string; price: number }>;
    bizGubun?: string;
    scope?: 'facility' | 'bizGubun' | 'all';
    year?: string;
  }) => {
    return api.post(`/bankbook/group-memos/${memberCode}`, data);
  },
  
  // 단일 키워드 학습
  learn: (data: {
    m_code: string | number;
    biz_gubun?: string;
    gubun: string;
    keyword: string;
    scode: string;
  }) => {
    return api.post('/bankbook/learn', data);
  },
  
  // 다중 키워드 일괄 학습
  learnBatch: (items: Array<{
    m_code: string | number;
    biz_gubun?: string;
    gubun: string;
    keyword: string;
    scode: string;
  }>) => {
    return api.post('/bankbook/learn-batch', { items });
  },
  
  // 적요로 계정 추론 (3단계 우선순위)
  infer: (memberCode: string | number, data: {
    memo: string;
    gubun: string;
    bizGubun?: string;
    scope?: 'facility' | 'bizGubun' | 'all';
    year?: string;
  }) => {
    return api.post(`/bankbook/infer/${memberCode}`, data);
  },
  
  // 학습 데이터 목록 조회
  getLearnList: (memberCode: string | number, params?: {
    bizGubun?: string;
    gubun?: string;
    scope?: 'facility' | 'bizGubun' | 'all';
    year?: string;
  }) => {
    return api.get(`/bankbook/learn-list/${memberCode}`, { params });
  },
  
  // 학습 데이터 삭제
  deleteLearn: (clIdx: number) => {
    return api.delete(`/bankbook/learn/${clIdx}`);
  },
  
  // 계정짝 (전 시설 공통, 연도별)
  getAccountPairs: (year?: string) => {
    return api.get('/bankbook/account-pairs', { params: { year } });
  },
  createAccountPair: (data: { year: string; incomeScode: string; expenseScode: string }) => {
    return api.post('/bankbook/account-pairs', data);
  },
  deleteAccountPair: (capIdx: number) => {
    return api.delete(`/bankbook/account-pairs/${capIdx}`);
  },

  // 계정조정 상태 (수입 잔액 + 짝)
  getAccountAdjustState: (memberCode: string | number, params: {
    year: string;
    month?: string;
    bizGubun?: string;
    bankCode?: string;
    asOfDate?: string;
  }) => {
    return api.get(`/bankbook/account-adjust-state/${memberCode}`, { params });
  },

  // 짝+잔액 자동계정조정 (빈 칸만). dryRun=true 이면 미리보기만
  autoAdjustAccounts: (memberCode: string | number, data: {
    year: string;
    month: string;
    bizGubun?: string;
    bankCode?: string;
    dryRun?: boolean;
  }) => {
    return api.post(`/bankbook/auto-adjust/${memberCode}`, data);
  },

  // 기억된 적요로 빈 계정만. dryRun=true 이면 미리보기만
  autoFillAccounts: (memberCode: string | number, data: {
    year: string;
    month: string;
    bizGubun?: string;
    bankCode?: string;
    dryRun?: boolean;
  }) => {
    return api.post(`/bankbook/auto-fill-accounts/${memberCode}`, data);
  },

  listAutoMatches: (memberCode: string | number, params: { kind: string; year?: string }) => {
    return api.get(`/bankbook/auto-match/${memberCode}`, { params });
  },
  saveAutoMatch: (memberCode: string | number, data: {
    kind: string;
    gubun: string;
    source: string;
    target: string;
  }) => {
    return api.post(`/bankbook/auto-match/${memberCode}`, data);
  },
  updateAutoMatch: (memberCode: string | number, id: number, data: { source: string; target: string }) => {
    return api.put(`/bankbook/auto-match/${memberCode}/${id}`, data);
  },
  deleteAutoMatch: (memberCode: string | number, id: number) => {
    return api.delete(`/bankbook/auto-match/${memberCode}/${id}`);
  },
  searchAutoMatchSources: (memberCode: string | number, q: string, kind: string) => {
    return api.get(`/bankbook/auto-match-sources/${memberCode}`, { params: { q, kind } });
  },
  importAutoMatches: (memberCode: string | number, data: { sourceCode: string; kinds: string[]; mode: 'add' | 'replace' }) => {
    return api.post(`/bankbook/auto-match-import/${memberCode}`, data);
  },
  keepAutoMatches: (memberCode: string | number, data: { kind: string }) => {
    return api.post(`/bankbook/auto-match-keep/${memberCode}`, data);
  },
  previewAutoMatches: (memberCode: string | number, data: {
    year: string;
    month: string;
    bizGubun?: string;
    bankCode?: string;
    kind: string;
    mode?: 'empty' | 'replace';
    cbIdxs?: number[];
  }) => {
    return api.post(`/bankbook/auto-match-preview/${memberCode}`, data);
  },

  autoFillRelAccounts: (memberCode: string | number, data: {
    year: string;
    month: string;
    bizGubun?: string;
    bankCode?: string;
    dryRun?: boolean;
  }) => {
    return api.post(`/bankbook/auto-fill-rel/${memberCode}`, data);
  },

  // 사용 가능한 상대계정 목록 조회 (수입 잔액 기반)
  getAvailableRelAccounts: (memberCode: string | number, date: string, price?: number, year?: string) => {
    return api.get(`/bankbook/available-rel-accounts/${memberCode}`, { 
      params: { date, price, year } 
    });
  },
  
  // 계정별 자금원천 조회 (자동 부여용)
  getFundingByAccount: (sname: string, trcode: string, year?: string) => {
    return api.get('/bankbook/funding-by-account', { 
      params: { sname, trcode, year } 
    });
  },
  
  // ========================================
  // 통장 관리 API
  // ========================================
  
  // 통장 목록 조회
  getBankList: (memberCode: string | number, includeInactive?: boolean) => {
    return api.get(`/bankbook/banks/${memberCode}`, { params: { includeInactive } });
  },
  
  // 통장 상세 조회
  getBank: (mbIdx: number) => {
    return api.get(`/bankbook/bank/${mbIdx}`);
  },
  
  // 통장 등록 (m_code = 시설 내부 PK)
  createBank: (data: {
    m_code: number; // 시설 내부 PK (caion_member.m_code)
    mb_alias: string;
    mb_bank_name: string;
    mb_account_no: string;
    mb_holder?: string;
    mb_purpose?: string;
    mb_is_default?: boolean;
    mb_biz_gubun?: string; // 사업구분 (시설의 활성화된 사업구분 중 하나)
    mb_start_date?: string; // 사용 시작월 (YYYY-MM)
    mb_end_date?: string; // 사용 종료월 (YYYY-MM)
  }) => {
    return api.post('/bankbook/bank', data);
  },
  
  // 통장 수정
  updateBank: (mbIdx: number, data: {
    mb_alias?: string;
    mb_bank_name?: string;
    mb_account_no?: string;
    mb_holder?: string;
    mb_purpose?: string;
    mb_is_default?: boolean;
    mb_is_active?: boolean;
    mb_biz_gubun?: string; // 사업구분
    mb_start_date?: string; // 사용 시작월 (YYYY-MM)
    mb_end_date?: string; // 사용 종료월 (YYYY-MM)
  }) => {
    return api.put(`/bankbook/bank/${mbIdx}`, data);
  },
  
  // 통장 삭제
  deleteBank: (mbIdx: number) => {
    return api.delete(`/bankbook/bank/${mbIdx}`);
  },
  
  // 통장 정렬 순서 변경
  reorderBanks: (memberCode: string | number, orders: Array<{ mb_idx: number; mb_sort_order: number }>) => {
    return api.put(`/bankbook/banks/reorder/${memberCode}`, { orders });
  },
  
  // 기본 통장 설정
  setDefaultBank: (mbIdx: number) => {
    return api.put(`/bankbook/bank/set-default/${mbIdx}`);
  },
  
  // 통장별 자금원천 일괄 적용
  applyFunding: (memberCode: string | number, data: {
    bankCode: string;
    funding: string;
    year?: string;
    month?: string;
    overwrite?: boolean;
  }) => {
    return api.put(`/bankbook/bank/apply-funding/${memberCode}`, data);
  },
  
  // 테스트 데이터 생성 (최고관리자 전용)
  generateTestData: (params: {
    memberCode: string | number;
    year: string;
    month: string;
    bankCode: string;
    bankAlias?: string;
    bizGubun?: string | null;
  }) => {
    return api.post(`/bankbook/generate-test-data/${params.memberCode}`, {
      year: params.year,
      month: params.month,
      bankCode: params.bankCode,
      bankAlias: params.bankAlias,
      bizGubun: params.bizGubun
    });
  },

  /** 에프엔에이 2026-09 업로드 테스트 엑셀 zip */
  downloadTestUploadPack: () => {
    return api.get('/bankbook/test-upload-pack', { responseType: 'blob' });
  },
  
  // ========================================
  // 분개(Split) 관련 API
  // ========================================
  
  // 분개 내역 조회
  getSplits: (cbIdx: number, year?: string) => {
    return api.get(`/bankbook/${cbIdx}/splits`, { params: { year } });
  },
  
  // 분개 생성/수정 (전체 교체)
  saveSplits: (cbIdx: number, lines: Array<{
    scode?: string;
    price: number;
    memo?: string;
    funding?: string;
    relAcc?: string;
    sortOrder?: number;
  }>) => {
    return api.put(`/bankbook/${cbIdx}/splits`, { lines });
  },

  // 분개 합치기 (원본 복원)
  clearSplits: (cbIdx: number) => {
    return api.delete(`/bankbook/${cbIdx}/splits`);
  },
  
  // 통장내역 목록 조회 (분개 여부 포함)
  getListWithSplits: (memberCode: string | number, params: {
    year: string;
    month: string;
    bizGubun?: string;
    bankCode?: string;
  }) => {
    return api.get(`/bankbook/list-with-splits/${memberCode}`, { params });
  },

  // ========================================
  // 현금출납부 (연간)
  // ========================================
  getCashReportYearSummary: (memberCode: string | number, params: {
    year: string;
    bizGubun?: string;
    bankCode?: string;
  }) => {
    return api.get(`/bankbook/report/cash-year-summary/${memberCode}`, { params });
  },

  getCashReportYearDetail: (memberCode: string | number, params: {
    year: string;
    bizGubun?: string;
    bankCode?: string;
  }) => {
    return api.get(`/bankbook/report/cash-year-detail/${memberCode}`, { params });
  },

  downloadCashReportYearPdf: (memberCode: string | number, params: {
    year: string;
    bizGubun?: string;
    bankCode?: string;
  }) => {
    return api.get(`/bankbook/report/cash-year-pdf/${memberCode}`, {
      params,
      responseType: 'blob',
      timeout: 120000
    });
  },

  downloadCashReportPdf: (memberCode: string | number, params: {
    year: string;
    month: string;
    bizGubun?: string;
    bankCode?: string;
  }) => {
    return api.get(`/bankbook/report/cash-pdf/${memberCode}`, {
      params,
      responseType: 'blob',
      timeout: 60000
    });
  },

  // 보고서 데이터 존재 여부
  getReportAvailability: (
    memberCode: string,
    params: { year: string; month: string; bizGubun?: string; bankCode?: string }
  ) => {
    return api.get(`/bankbook/report/availability/${memberCode}`, { params });
  },

  getAccountingAnalysis: (
    memberCode: string,
    params: {
      year: string;
      month: string;
      bizGubun?: string;
      bankCode?: string;
      periodType?: string;
      startMonth?: number;
      endMonth?: number;
    }
  ) => {
    return api.get(`/bankbook/report/analysis/${memberCode}`, { params });
  },

  getAccountingAnalysisReference: (
    memberCode: string,
    params: {
      year: string;
      month: string;
      bizGubun?: string;
      bankCode?: string;
      periodType?: string;
      startMonth?: number;
      endMonth?: number;
    }
  ) => {
    return api.get(`/bankbook/report/analysis-reference/${memberCode}`, { params });
  },

  /** 세입/세출 결산 비교 (1월~해당월) */
  getSettlementView: (
    memberCode: string,
    params: {
      year: string;
      month: string;
      bizGubun?: string;
      bankCode?: string | null;
    }
  ) => {
    return api.get(`/bankbook/report/settlement/${memberCode}`, {
      params: {
        year: params.year,
        month: params.month,
        bizGubun: params.bizGubun,
        bankCode: params.bankCode || undefined,
      },
    });
  },

  saveAccountingAnalysisMemo: (
    memberCode: string,
    data: {
      year: string;
      month: string;
      bizGubun?: string;
      title: string;
      memo: string;
      periodType?: string;
      startMonth?: number;
      endMonth?: number;
    }
  ) => {
    return api.put(`/bankbook/report/analysis-memo/${memberCode}`, data);
  },

  // 결의서 PDF (수입/지출/반납)
  getResolutionReportPdf: (
    memberCode: string,
    reportKey: string,
    params: {
      year: string;
      month: string;
      bizGubun?: string;
      bankCode?: string;
      approvalLabels?: string;
      printerName?: string;
      periodType?: string;
      startMonth?: number;
      endMonth?: number;
    }
  ) => {
    return api.get(`/bankbook/report/resolution-pdf/${memberCode}/${reportKey}`, {
      params,
      responseType: 'blob',
      timeout: 180000
    });
  },

  // 결재자 정보
  getFinanceInfo: (memberCode: string) => {
    return api.get(`/bankbook/finance-info/${memberCode}`);
  },

  upsertFinanceInfo: (
    memberCode: string,
    data: {
      chief_name?: string;
      paym_name?: string;
      income_name?: string;
      outcome_name?: string;
      approval_labels?: string;
      printer_name?: string;
    }
  ) => {
    return api.put(`/bankbook/finance-info/${memberCode}`, data);
  },

  // 결재 도장
  getStampList: (memberCode: string) => {
    return api.get(`/bankbook/stamp-list/${memberCode}`);
  },

  getStampImage: (memberCode: string, stampType: string) => {
    return api.get(`/bankbook/stamp/${memberCode}/${stampType}`, {
      responseType: 'blob',
      timeout: 30000,
    });
  },

  uploadStamp: (memberCode: string, stampType: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('memberCode', memberCode);
    formData.append('stampType', stampType);
    return api.post('/bankbook/stamp', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },

  deleteStamp: (memberCode: string, stampType: string) => {
    return api.delete(`/bankbook/stamp/${memberCode}/${stampType}`);
  },
};

// ========================================
// 수급자 관리 API
// ========================================
export const beneficiaryApi = {
  // 수급자 목록 — yearMonth 있으면 LTCS 자동수집+직접등록 UNION
  getList: (memberCode: string | number, params?: {
    search?: string;
    status?: string;
    bizGubun?: string;
    yearMonth?: string;
    asOfToday?: boolean;
    page?: number;
    limit?: number;
  }) => {
    return api.get(`/beneficiary/member/${memberCode}`, { params });
  },

  getLtcsById: (memberCode: string | number, source: 'auto' | 'manual', clsrIdx: number, yearMonth?: string) => {
    return api.get(`/beneficiary/member/${memberCode}/${source}/${clsrIdx}`, {
      params: yearMonth ? { yearMonth } : undefined,
    });
  },
  
  // 수급자 통계 조회
  getStats: (memberCode: string | number, bizGubun?: string) => {
    const params: Record<string, string> = {};
    if (bizGubun) params.bizGubun = bizGubun;
    return api.get(`/beneficiary/member/${memberCode}/stats`, { params });
  },
  
  // 수급자 단건 조회
  getById: (cbIdx: number) => {
    return api.get(`/beneficiary/${cbIdx}`);
  },
  
  /** 직접등록(LTCS sudong) — useLtcs: true */
  create: (memberCode: string | number, data: Record<string, unknown>) => {
    return api.post(`/beneficiary/member/${memberCode}`, data);
  },

  updateLtcsManual: (memberCode: string | number, clsrIdx: number, data: Record<string, unknown>) => {
    return api.put(`/beneficiary/member/${memberCode}/manual/${clsrIdx}`, data);
  },

  /** LTCS 수급자 수정 — auto/manual 모두 */
  updateLtcsInfo: (memberCode: string | number, source: 'auto' | 'manual', clsrIdx: number, data: Record<string, unknown>) => {
    return api.put(`/beneficiary/member/${memberCode}/${source}/${clsrIdx}`, data);
  },

  /** 수급자 이용·등급 이력 */
  getHistory: (memberCode: string | number, source: 'auto' | 'manual', clsrIdx: number) => {
    return api.get(`/beneficiary/member/${memberCode}/${source}/${clsrIdx}/history`);
  },

  /** 수급자 주민번호 뒷자리 복호화 조회 */
  revealSsn: (memberCode: string | number, source: 'auto' | 'manual', clsrIdx: number) => {
    return api.get(`/beneficiary/member/${memberCode}/${source}/${clsrIdx}/ssn`);
  },

  deleteLtcsManual: (memberCode: string | number, clsrIdx: number) => {
    return api.delete(`/beneficiary/member/${memberCode}/manual/${clsrIdx}`);
  },
  
  // 수급자 일괄 등록 (엑셀 업로드)
  bulkCreate: (memberCode: string | number, formData: FormData) => {
    return api.post(`/beneficiary/member/${memberCode}/bulk`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000
    });
  },
  
  // 수급자 수정 (caion_beneficiary)
  update: (memberCode: string | number, cbIdx: number, data: {
    cb_name?: string;
    cb_recipient_id?: string;
    cb_ltc_no?: string;
    cb_gender?: string;
    cb_birth_date?: string;
    cb_use_status?: string;
    cb_status?: string;
    cb_enter_date?: string;
    cb_leave_date?: string;
    cb_receipt_date?: string;
    cb_phone?: string;
    cb_mobile?: string;
    cb_addr?: string;
    cb_memo?: string;
    cb_ssis_no?: number;
  }) => {
    return api.put(`/beneficiary/member/${memberCode}/${cbIdx}`, data);
  },
  
  // 수급자 삭제
  delete: (cbIdx: number) => {
    return api.delete(`/beneficiary/${cbIdx}`);
  },
  
  // 엑셀 양식 다운로드
  downloadTemplate: async () => {
    const response = await api.get('/beneficiary/template', {
      responseType: 'blob'
    });
    
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'beneficiary_template.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return response;
  },
  
  // 적요에서 수급자 매칭
  matchFromMemo: (mCode: number, items: { idx: number; memo: string }[]) => {
    return api.post('/beneficiary/match', { m_code: mCode, items });
  },
  
  // 수급자ID 자동매칭 (caion_ltcs_recipient_change 테이블에서)
  matchRecipientId: (memberCode: string | number, bizGubun?: string) => {
    const params: Record<string, string> = {};
    if (bizGubun) params.bizGubun = bizGubun;
    return api.get(`/beneficiary/member/${memberCode}/match-recipient-id`, { params });
  },
  
  // 수급자ID 일괄 업데이트
  updateRecipientIds: (memberCode: string | number, updates: { cb_idx: number; new_id: string }[]) => {
    return api.post(`/beneficiary/member/${memberCode}/update-recipient-ids`, { updates });
  },
  
  // LTCS 데이터와 매칭 정보 조회 (신규 등록 가능 + ID 업데이트 가능)
  getLtcsMatchData: (memberCode: string | number, bizGubun?: string) => {
    const params: Record<string, string> = {};
    if (bizGubun) params.bizGubun = bizGubun;
    return api.get(`/beneficiary/member/${memberCode}/ltcs-match-data`, { params });
  },
  
  // LTCS 데이터에서 수급자 일괄 등록
  importFromLtcs: (memberCode: string | number, data: {
    bizGubun?: string;
    items: {
      clrc_idx: number;
      cb_name: string;
      cb_recipient_id: string;
      cb_enter_date?: string;
      cb_leave_date?: string;
    }[];
  }) => {
    return api.post(`/beneficiary/member/${memberCode}/import-from-ltcs`, data);
  },
  
  // 엑셀 일괄 업로드
  bulkUpload: (memberCode: string | number, formData: FormData) => {
    return api.post(`/beneficiary/member/${memberCode}/bulk`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // 롱텀케어 수급자구분변경이력 동기화 데이터 조회 [구버전]
  getLtcsRecipientChangeSyncData: (memberCode: string | number, params: { bizGubun?: string; yearMonth?: string }) => {
    return api.get(`/beneficiary/member/${memberCode}/ltcs-recipient-change`, { params });
  },
  
  // 롱텀케어 수급자구분변경이력으로 동기화 실행 [구버전]
  syncFromLtcsRecipientChange: (memberCode: string | number, data: {
    bizGubun?: string;
    toAdd: any[];
    toUpdate: any[];
  }) => {
    return api.post(`/beneficiary/member/${memberCode}/sync-ltcs-recipient-change`, data);
  },
  
  // 롱텀케어 수급자 정보 동기화 데이터 조회 [신버전]
  getLtcsRecipientInfoSyncData: (memberCode: string | number, params: { bizGubun?: string; yearMonth?: string }) => {
    return api.get(`/beneficiary/member/${memberCode}/ltcs-recipient-info`, { params });
  },
  
  // 롱텀케어 수급자 정보로 동기화 실행 [신버전]
  syncFromLtcsRecipientInfo: (memberCode: string | number, data: {
    bizGubun?: string;
    toAdd: any[];
    toUpdate: any[];
  }) => {
    return api.post(`/beneficiary/member/${memberCode}/sync-ltcs-recipient-info`, data);
  },

  // ============ 데이터 동기화 (새 방식 - 종사자 페이지와 통일) ============
  
  // 크롤링 데이터 목록 조회
  getSyncData: (memberCode: number | string, bizGubun?: string) => {
    return api.get(`/beneficiary/member/${memberCode}/sync-data`, { params: { bizGubun } });
  },

  // 크롤링 요청 등록
  requestCrawl: (memberCode: number | string, bizGubun?: string) => {
    return api.post(`/beneficiary/member/${memberCode}/request-crawl`, { bizGubun });
  },

  // 크롤링 요청 상태 조회 (폴링용)
  getRequestStatus: (memberCode: number | string, cmqIdx: number) => {
    return api.get(`/beneficiary/member/${memberCode}/request-status/${cmqIdx}`);
  },

  // 전체 교체 (크롤링 데이터로 수급자 목록 전체 교체)
  syncReplace: (memberCode: number | string, data: { bizGubun?: string; items: any[] }) => {
    return api.post(`/beneficiary/member/${memberCode}/sync-replace`, data);
  },

  // 매칭 업데이트 (인정번호가 같은 수급자만 업데이트)
  syncUpdate: (memberCode: number | string, data: { bizGubun?: string; items: any[] }) => {
    return api.post(`/beneficiary/member/${memberCode}/sync-update`, data);
  }
};

// ========================================
// 종사자(직원) API
// MemberDetailEmployee.vue 페이지 전용
// ========================================
export const employeeApi = {
  /** 활성 직종 목록 (선택 UI 공통) */
  getJobTypes: () => api.get('/employee/job-types'),

  // 사원(요양보호사) 목록 — 자동수집+직접등록 UNION, yearMonth(YYYY-MM) 필수 권장
  getList: (memberCode: string | number, params?: {
    search?: string;
    status?: string; // 현원 | 퇴사 | 입사예정
    yearMonth?: string;
    includeAll?: boolean;
    asOfToday?: boolean;
    groupSamePerson?: boolean;
    /** 성명·생년월일당 1명. 입사일은 직접등록이 있으면 그 날짜 */
    preferJoinDate?: boolean;
    page?: number;
    limit?: number;
  }) => {
    return api.get(`/employee/member/${memberCode}`, { params });
  },

  getById: (memberCode: string | number, source: 'auto' | 'manual', clciIdx: number, yearMonth?: string) => {
    return api.get(`/employee/member/${memberCode}/${source}/${clciIdx}`, {
      params: yearMonth ? { yearMonth } : undefined,
    });
  },

  /** 직접등록(수동) — 필수: 성명, 생년월일, 주민뒷자리, 직종, 휴대폰, 입사일 (이메일은 선택)
   * fromClaim:true 이면 청구내역 간편등록(성명·생년월일·직종만 필수)
   */
  create: (memberCode: string | number, data: {
    clci_name: string;
    clci_birth_date: string;
    clci_job_type: string;
    clci_ssn_back?: string;
    clci_phone?: string;
    clci_email?: string;
    clci_work_start_date?: string;
    clci_work_end_date?: string;
    fromClaim?: boolean;
  }) => {
    return api.post(`/employee/member/${memberCode}`, data);
  },

  update: (
    memberCode: string | number,
    clciIdx: number,
    data: Record<string, unknown>,
    source: 'auto' | 'manual' = 'manual'
  ) => {
    return api.put(`/employee/member/${memberCode}/${source}/${clciIdx}`, data);
  },

  /** 주민번호 뒷자리 복호화 조회 */
  revealSsn: (memberCode: string | number, source: 'auto' | 'manual', clciIdx: number) => {
    return api.get(`/employee/member/${memberCode}/${source}/${clciIdx}/ssn`);
  },

  /** 앱 회원정보와 직원정보가 다른 항목 */
  getProfileLink: (memberCode: string | number, source: 'auto' | 'manual', clciIdx: number) => {
    return api.get(`/employee/member/${memberCode}/${source}/${clciIdx}/profile-link`);
  },

  applyProfileLink: (
    memberCode: string | number,
    source: 'auto' | 'manual',
    clciIdx: number,
    data: { role: string; choices: Record<string, 'app' | 'web'> }
  ) => {
    return api.post(`/employee/member/${memberCode}/${source}/${clciIdx}/profile-link`, data);
  },

  delete: (memberCode: string | number, clciIdx: number) => {
    return api.delete(`/employee/member/${memberCode}/manual/${clciIdx}`);
  },
  
  // 엑셀 양식 다운로드
  downloadTemplate: async () => {
    const response = await api.get('/employee/template', {
      responseType: 'blob'
    });
    
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'employee_template.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return response;
  },
  
  // 엑셀 일괄 업로드
  bulkUpload: (memberCode: string | number, formData: FormData) => {
    return api.post(`/employee/member/${memberCode}/bulk`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// 수금관리 API
export const collectionApi = {
  // 시설별 수금 내역 조회 (월별)
  getByMember: (mCode: number, params?: { year?: number; month?: number; bizGubun?: string }) => {
    return api.get(`/collection/member/${mCode}`, { params });
  },
  
  // 수급자별 수금 내역 조회
  getByBeneficiary: (cbIdx: number, params?: { year?: number; month?: number; includeDeleted?: boolean }) => {
    return api.get(`/collection/beneficiary/${cbIdx}`, { params });
  },
  
  // 수금 내역 등록
  create: (data: {
    m_code: number;
    cb_idx: number;
    cc_year: number;
    cc_month: number;
    cc_scode: string;
    cc_sname: string;
    cc_charge?: number;
    cc_charge_date?: string | null;
    cc_deposit?: number;
    cc_deposit_date?: string | null;
    cc_memo?: string;
    cc_biz_gubun?: string;
  }) => {
    return api.post('/collection', data);
  },
  
  // 수금 내역 수정
  update: (ccIdx: number, data: {
    cc_charge?: number;
    cc_charge_date?: string | null;
    cc_deposit?: number;
    cc_deposit_date?: string | null;
    cc_memo?: string;
    cc_deposit_detail?: string | null;
  }) => {
    return api.put(`/collection/${ccIdx}`, data);
  },
  
  // 수금 내역 삭제 (soft delete - cc_ddate에 현재시간)
  remove: (ccIdx: number) => {
    return api.delete(`/collection/${ccIdx}`);
  },
  
  // 삭제된 수금 내역 복원
  restore: (ccIdx: number) => {
    return api.post(`/collection/${ccIdx}/restore`);
  },
  
  // 시설별 수금 요약
  getSummary: (mCode: number, params?: { year?: number; month?: number }) => {
    return api.get(`/collection/summary/${mCode}`, { params });
  },
  
  // 수급자별 이전 월 누적 미수금 조회
  getPreviousUnpaid: (mCode: number, params: { year: number; month: number }) => {
    return api.get(`/collection/previous-unpaid/${mCode}`, { params });
  },
  
  // 수금관리용 계정과목 조회 (본인부담금 분개 항목들)
  getFinanceCodes: (year: number) => {
    return api.get(`/collection/finance-codes/${year}`);
  },
  
  // 수납관리 엑셀 업로드
  uploadExcel: (mCode: number, data: {
    items: Array<{
      name: string;
      ltcNo?: string;
      itemName: string;
      charge: number;
      chargeDate?: string;
      memo?: string;
    }>;
    year: number;
    month: number;
    bizGubun?: string;
  }) => {
    return api.post(`/collection/upload-excel/${mCode}`, data);
  },
  
  // 엑셀 양식 정보 조회
  getExcelTemplate: () => {
    return api.get('/collection/excel-template');
  },
  
  // 통장내역 본인부담금수입과 수납 데이터 동기화
  syncDeposit: (mCode: number, data: {
    year: number;
    month: number;
    deposits: Array<{
      cb_idx: number;
      amount: number;
      date?: string;
    }>;
    bizGubun?: string;
  }) => {
    return api.post(`/collection/sync-deposit/${mCode}`, data);
  },
  
  // 수급자의 입금액 초기화 (통장내역 삭제 시 호출)
  clearDeposits: (mCode: number, data: {
    cbIdxList: number[];
    year: number;
    month: number;
    bizGubun?: string;
  }) => {
    return api.post(`/collection/clear-deposits/${mCode}`, data);
  },
  
  // 누적 미수금 조회 (설정된 연월까지)
  getCumulativeUnpaid: (mCode: number, params?: { year?: number; month?: number; bizGubun?: string }) => {
    return api.get(`/collection/cumulative-unpaid/${mCode}`, { params });
  },
  
  // 미수금 이력 조회 (검색, 기간 필터 지원)
  getUnpaidHistory: (mCode: number, params?: {
    year?: number;
    month?: number;
    searchName?: string;
    startDate?: string;
    endDate?: string;
    bizGubun?: string;
    page?: number;
    limit?: number;
  }) => {
    return api.get(`/collection/unpaid-history/${mCode}`, { params });
  }
};

// ========================================
// 임금 마감 API (caion_wage_magam)
// ========================================
export const wageMagamApi = {
  getStatus: (params: { mCode: string; yearMonth: string; bizGubun: string }) => {
    return api.get('/wage-magam/status', { params });
  },
  close: (data: { mCode: string; yearMonth: string; bizGubun: string; memo?: string }) => {
    return api.post('/wage-magam/close', data);
  },
  release: (data: { mCode: string; yearMonth: string; bizGubun: string }) => {
    return api.post('/wage-magam/release', data);
  },
  previewSlipBroadcast: (params: { mCode: string; yearMonth: string; bizGubun: string }) => {
    return api.get('/wage-magam/slip-broadcast-preview', { params });
  },
  sendSlipBroadcast: (data: {
    mCode: string;
    yearMonth: string;
    bizGubun: string;
    title?: string;
    /** 체크된 요보만 발송 (없으면 전체 — 기존 동작) */
    targetCayIdxs?: number[];
  }) => {
    return api.post('/wage-magam/slip-broadcast', data);
  },
};

// ========================================
// 마감 관리 API
// ========================================
export const closingApi = {
  // 마감 상태 조회
  getStatus: (memberCode: string | number, params: {
    year: string | number;
    month: string | number;
    type: string;
    bizGubun?: string;
    bankCode?: string;
  }) => {
    return api.get(`/closing/status/${memberCode}`, { params });
  },
  
  // 통장별 마감 요약 상태 조회 (전체 통장 선택 시 - 월별통장내역 페이지용)
  getBankClosingSummary: (memberCode: string | number, params: {
    year: string | number;
    month: string | number;
    type: string;
    bizGubun?: string;
  }) => {
    return api.get(`/closing/bank-summary/${memberCode}`, { params });
  },
  
  // 마감 처리
  close: (memberCode: string | number, data: {
    year: string | number;
    month: string | number;
    type: string;
    status: string;
    bizGubun?: string;
    bankCode?: string;
    memo?: string;
    checkPairs?: boolean;
  }) => {
    return api.post(`/closing/close/${memberCode}`, data);
  },
  
  // 마감 해제
  unclose: (memberCode: string | number, data: {
    year: string | number;
    month: string | number;
    type: string;
    status: string;
    bizGubun?: string;
    bankCode?: string;
  }) => {
    return api.post(`/closing/unclose/${memberCode}`, data);
  },
  
  // 유효성 검사
  validate: (memberCode: string | number, params: {
    year: string | number;
    month: string | number;
    type: string;
    status: string;
    bizGubun?: string;
    bankCode?: string;
    checkPairs?: boolean;
  }) => {
    return api.get(`/closing/validate/${memberCode}`, { params });
  },
  
  // 마감 이력 조회
  getHistory: (memberCode: string | number, params?: {
    year?: string | number;
    month?: string | number;
    type?: string;
    bizGubun?: string;
    bankCode?: string;
    includeDeleted?: boolean;
  }) => {
    return api.get(`/closing/history/${memberCode}`, { params });
  },
  
  // ========================================
  // 희망이음 업로드 API
  // ========================================
  
  // 희망이음 요청 (등록/삭제/수정)
  heumieumRequest: (memberCode: string | number, data: {
    year: string | number;
    month: string | number;
    bizGubun?: string;
    bankCode: string;
    bankAlias?: string;
    memo?: string;
    requestType?: 'register' | 'delete' | 'modify';
  }) => {
    return api.post(`/closing/heumieum/request/${memberCode}`, data);
  },
  
  // 희망이음 업로드 완료 처리
  heumieumComplete: (memberCode: string | number, data: {
    year: string | number;
    month: string | number;
    bankCode: string;
    completeMemo?: string;
  }) => {
    return api.post(`/closing/heumieum/complete/${memberCode}`, data);
  },
  
  // 희망이음 업로드 요청 취소
  heumieumCancel: (memberCode: string | number, data: {
    year: string | number;
    month: string | number;
    bankCode: string;
  }) => {
    return api.post(`/closing/heumieum/cancel/${memberCode}`, data);
  },
  
  // 희망이음 완료 -> 요청중으로 되돌리기 (관리자 이상 권한)
  heumieumRevertToRequested: (memberCode: string | number, data: {
    year: string | number;
    month: string | number;
    bankCode: string;
  }) => {
    return api.post(`/closing/heumieum/revert-to-requested/${memberCode}`, data);
  },
  
  // 희망이음 업로드 상태 조회 (통장별)
  heumieumStatus: (memberCode: string | number, params: {
    year: string | number;
    month: string | number;
    bizGubun?: string;
  }) => {
    return api.get(`/closing/heumieum/status/${memberCode}`, { params });
  },
  
  // 희망이음 업로드 대기 목록 (관리자용)
  heumieumPending: (params?: {
    year?: string | number;
    month?: string | number;
  }) => {
    return api.get(`/closing/heumieum/pending`, { params });
  },

  // 슈퍼바이저 전용 — 희망이음 업로드 요청 현황
  heumieumRequestBoard: () => {
    return api.get('/closing/heumieum/board');
  },
  
  // 마감요청내역 - 시설 전체 기간 통장별 마감 현황
  getSummary: (memberCode: string | number, params?: {
    bizGubun?: string;
  }) => {
    return api.get(`/closing/summary/${memberCode}`, { params });
  },
  
  // 마감요청내역 - 메모 저장/수정
  updateMemo: (memberCode: string | number, data: {
    year: number;
    month: number;
    bankCode: string;
    memo: string;
    bizGubun?: string;
  }) => {
    return api.post(`/closing/memo/${memberCode}`, data);
  },
  
  // 희망이음 요청 이력 조회
  getHeumieumHistory: (memberCode: string | number, params?: {
    year?: number;
    month?: number;
    bankCode?: string;
    bizGubun?: string;
  }) => {
    return api.get(`/closing/heumieum/history/${memberCode}`, { params });
  }
};

// 요양시설 임금관리 API (/api/common-wage/*)
export const commonWageApi = {
  /** 임금대장 컬럼 사전 (카이온 워딩 ↔ 들어온 헤더) */
  getColumnDict: () => {
    return api.get('/wage-column-dict');
  },

  // 종사자 + 임금 목록
  getList: (mCode: number | string, params: {
    year: number | string;
    month: number | string;
    bizGubun?: string;
  }) => {
    return api.get(`/common-wage/list/${mCode}`, { params });
  },

  // 단일 임금 데이터 조회 (추후 구현)
  getDetail: (ccwdIdx: number | string) => {
    return api.get(`/common-wage/${ccwdIdx}`);
  },

  /** body 필드명 = DB 컬럼명 (cwhd_*) — @/utils/commonWageMapper CommonWageSavePayload */
  save: (data: import('@/utils/commonWageMapper').CommonWageSavePayload) => {
    return api.post('/common-wage/save', data);
  },

  delete: (ccwdIdx: number | string) => {
    return api.delete(`/common-wage/${ccwdIdx}`);
  },

  /** 목록에서 제거한 당월 임금행 복원 */
  restore: (ccwdIdx: number | string) => {
    return api.post(`/common-wage/${ccwdIdx}/restore`);
  },

  copyFromPrev: (data: {
    mCode: number | string;
    bizGubun?: string;
    year: number;
    month: number;
  }) => {
    return api.post('/common-wage/copy-from-prev', data);
  },

  deleteAllMonth: (data: {
    mCode: number | string;
    bizGubun?: string;
    year: number;
    month: number;
  }) => {
    return api.post('/common-wage/delete-all-month', data);
  },

  getWageSlipPdf: (data: {
    mCode: number | string;
    bizGubun?: string;
    year: number;
    month: number;
  }) => {
    return api.post('/common-wage/wage-slip-pdf', data, {
      responseType: 'blob'
    });
  },

  getTaxRate: (year: number | string) => {
    return api.get(`/common-wage/tax-rate/${year}`);
  },

  saveTaxRate: (
    year: number | string,
    data: {
      minHourlyWage: number;
      rateYeon: number;
      rateGun: number;
      rateGo: number;
      rateJang: number;
      rateSil: number;
      rateSan: number;
      laborCostRate: number;
      memo?: string | null;
    }
  ) => {
    return api.put(`/common-wage/tax-rate/${year}`, data);
  },

  getIncomeTax: (year: number | string) => {
    return api.get(`/common-wage/income-tax/${year}`);
  },

  getYearlyNontax: (params: {
    mCode: string;
    name: string;
    birth?: string | null;
    year: number | string;
    month: number | string;
  }) => {
    return api.get('/common-wage/yearly-nontax', { params });
  },

  getYearlyNontaxBatch: (data: {
    mCode: string;
    empList: Array<{ name: string; birth?: string | null; empMatchKey?: string }>;
    year: number | string;
    month: number | string;
  }) => {
    return api.post('/common-wage/yearly-nontax-batch', data);
  },

  getPrevLunch: (params: {
    mCode: string;
    name: string;
    birth?: string | null;
    year: number | string;
    month: number | string;
  }) => {
    return api.get('/common-wage/prev-lunch', { params });
  },

  getPrevMonthWage: (params: {
    mCode: string;
    name: string;
    birth?: string | null;
    year: number | string;
    month: number | string;
    bizGubun?: string;
  }) => {
    return api.get('/common-wage/prev-month-wage', { params });
  },

  getMonthlySetting: (mCode: number | string, year: number | string, month: number | string, bizGubun?: string) => {
    return api.get(`/common-wage/monthly-setting/${mCode}/${year}/${month}`, {
      params: { bizGubun: bizGubun || '' }
    });
  },

  saveMonthlySetting: (data: {
    mCode: number | string;
    bizGubun?: string;
    year: number;
    month: number;
    payDate?: string | null;
    sanRate?: number | null;
  }) => {
    return api.post('/common-wage/monthly-setting', data);
  },

  postTransactionPdf: (data: {
    mCode: string | number;
    year: number;
    month: number;
    memberName?: string;
    bizGubun?: string;
    writeDate?: string;
    wageCount?: number;
    wageUnitPrice?: number;
    wageBigo?: string;
  }) => {
    return api.post('/common-wage/transaction-pdf', data);
  },

  /** 임금 엑셀 마이그레이션 (관리자) */
  downloadMigrationTemplate: () => {
    return api.get('/common-wage/migration/template', { responseType: 'blob' });
  },

  validateMigrationExcel: (formData: FormData) => {
    return api.post('/common-wage/migration/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  importMigration: (data: {
    mCode: string;
    yearMonth: string;
    bizGubun?: string;
    rows: unknown[];
    registerMissingEmployees?: boolean;
  }) => {
    return api.post('/common-wage/migration/import', data);
  },
};

/** @deprecated commonWageApi 사용 */
export const wageDataApi = commonWageApi;

// 연간 예산 작성 API
export const budgetApi = {
  getFinanceCodes: (params: { year: string; trcode?: string }) => {
    return api.get('/budget/finance-codes', { params });
  },
  getLines: (params: {
    memberCode: number | string;
    year: string | number;
    gubun?: string | null;
    budClass?: string;
    bizGubun?: string;
  }) => {
    return api.get('/budget', { params });
  },
  save: (data: {
    memberCode: number;
    year: string | number;
    budClass?: string;
    bizGubun?: string;
    budgetList: Record<string, unknown>[];
  }) => {
    return api.post('/budget/save', data);
  },
  deleteMultiple: (memberCode: number, hbIdxList: number[]) => {
    return api.post('/budget/delete-multiple', { memberCode, hbIdxList });
  },
  deleteYear: (data: {
    memberCode: number;
    year: string | number;
    budClass?: string;
    bizGubun?: string;
  }) => {
    return api.post('/budget/delete', data);
  },
  insertLine: (data: {
    memberCode: number;
    year: string | number;
    budClass?: string;
    bizGubun?: string;
    trcode: string;
    scode: string;
    payRel?: string;
  }) => {
    return api.post('/budget/insert', data);
  },
  /** 본인부담금(112)·장기요양급여(611) 수가 셀렉트 */
  getSuga: (params: { year: string | number; bizGubun?: string }) => {
    return api.get('/budget/suga', { params });
  },
  getSugaInfoYears: () => api.get('/budget/suga-info/years'),
  getSugaInfoList: (params: { year: string | number }) => api.get('/budget/suga-info/list', { params }),
  postSugaInfo: (data: {
    bizGubun: string;
    year: number;
    rank: string;
    isUse?: string;
    basisYn?: string;
    grossFee?: string | number;
    copayFee?: string | number;
    sort?: number;
  }) => api.post('/budget/suga-info', data),
  patchSugaInfo: (
    idx: number,
    data: Partial<{
      bizGubun: string;
      year: number;
      rank: string;
      isUse: string;
      basisYn: string;
      grossFee: string | number;
      copayFee: string | number;
      sort: number;
    }>
  ) => api.patch(`/budget/suga-info/${idx}`, data),
  deleteSugaInfo: (idx: number) => api.delete(`/budget/suga-info/${idx}`),
  copySugaInfoYear: (data: { fromYear: number; toYear: number }) =>
    api.post('/budget/suga-info/copy-year', data),
  /** 급여 연동 사회보험 요율 (bizGubun 시 사업구분별 인건비 기준 비율 적용 가능) */
  getTaxRates: (params: { year: string | number; bizGubun?: string }) => {
    return api.get('/budget/tax-rates', { params });
  },
  /** 저장된 추경 차수(본예산='') */
  getBudClasses: (params: { memberCode: number | string; year: string | number; bizGubun?: string }) => {
    return api.get('/budget/bud-classes', { params });
  },
  /** caion_budget_line에서 동일 시설·연도·사업구분의 mbl_bud_class만 바꾼 행 복제 */
  cloneBudClass: (data: {
    memberCode: number;
    year: string | number;
    budClassFrom: string;
    budClassTo: string;
    bizGubun: string;
  }) => {
    return api.post<{
      success: boolean;
      copied?: number;
      message?: string;
      warnings?: string[];
    }>('/budget/clone-bud-class', data);
  },
  /** 기존 데이터의 급여-사회보험-퇴직금 연결 마이그레이션 */
  migratePayRel: (data: {
    memberCode?: number;
    year?: number;
    bizGubun?: string;
    budClass?: string;
    dryRun?: boolean;
  }) => {
    return api.post<{
      success: boolean;
      dryRun: boolean;
      updated: number;
      details: string[];
      message: string;
    }>('/budget/migrate-pay-rel', data);
  },

  /** 전년도(본예산) / 직전차수(추경) 예산 복사 — 현재 시트 덮어씀 */
  loadRecent: (data: {
    memberCode: number | string;
    year: string | number;
    budClass?: string;
    bizGubun?: string;
  }) => {
    return api.post<{ success: boolean; copied?: number; message?: string }>('/budget/load-recent', data);
  },

  /** EXCEL 예산서 다운로드 */
  downloadExcel: (params: {
    memberCode: number | string;
    year: string | number;
    budClass?: string;
    bizGubun?: string;
  }) => {
    return api.get('/budget/excel', { params, responseType: 'blob' });
  },

  /** EXCEL 희망이음 업로드 양식 */
  downloadHopeExcel: (params: {
    memberCode: number | string;
    year: string | number;
    budClass?: string;
    bizGubun?: string;
  }) => {
    return api.get('/budget/excel-hope', { params, responseType: 'blob' });
  },

  /** 임직원 보수 일람표 */
  getEmployeeSalary: (params: {
    memberCode: number | string;
    year: string | number;
    budClass?: string;
    bizGubun?: string;
  }) => {
    return api.get<{ success: boolean; data: Array<{
      name: string;
      job: string;
      expense_type: string;
      biz_gubun: string;
      pay: number;
      out_pay: number;
      social_pay: number;
      total_pay: number;
      is_ceo: boolean;
    }> }>('/budget/employee-salary', { params });
  },

  /** 임직원 보수 일람표 엑셀 */
  downloadEmployeeSalaryExcel: (params: {
    memberCode: number | string;
    year: string | number;
    budClass?: string;
    bizGubun?: string;
  }) => {
    return api.get('/budget/employee-salary-excel', { params, responseType: 'blob' });
  },
};

// 예산추경 API
export const budgetRevisionApi = {
  // 추경 목록 조회
  getList: (
    mCode: number,
    params?: { year?: number; month?: number; bizGubun?: string; budClass?: string }
  ) => {
    return api.get(`/budget-revision/member/${mCode}`, { params });
  },
  
  // 추경 상세 조회
  getById: (brIdx: number) => {
    return api.get(`/budget-revision/${brIdx}`);
  },
  
  // 추경 등록
  create: (data: {
    m_code: number;
    br_gubun: 'IN' | 'OUT';
    br_date: string;
    br_scode: string;
    br_sname?: string;
    br_type: string;
    br_original_amount?: number;
    br_change_amount: number;
    br_revised_amount?: number;
    br_memo?: string;
    br_year: number;
    br_month: number;
    br_biz_gubun?: string;
    br_bud_class?: string;
  }) => {
    return api.post('/budget-revision', data);
  },
  
  // 추경 수정
  update: (brIdx: number, data: {
    br_gubun?: 'IN' | 'OUT';
    br_date?: string;
    br_scode?: string;
    br_sname?: string;
    br_type?: string;
    br_original_amount?: number;
    br_change_amount?: number;
    br_revised_amount?: number;
    br_memo?: string;
    br_bud_class?: string;
  }) => {
    return api.put(`/budget-revision/${brIdx}`, data);
  },
  
  // 추경 삭제
  remove: (brIdx: number) => {
    return api.delete(`/budget-revision/${brIdx}`);
  },
  
  // 추경 요약 (연간)
  getSummary: (mCode: number, params: { year: number; bizGubun?: string; budClass?: string }) => {
    return api.get(`/budget-revision/summary/${mCode}`, { params });
  }
};

// 방문요양/방문목욕 임금관리 API
export const homeWageApi = {
  // ========================================
  // 프로세스 관련
  // ========================================
  
  // 프로세스 상태 조회
  getProcessStatus: (params: { mCode: number | string; bizGubun?: string; yearMonth: string }) => {
    return api.get('/home-wage/process/status', { params });
  },
  
  // 프로세스 초기화
  initProcess: (data: { mCode: number | string; bizGubun?: string; yearMonth: string }) => {
    return api.post('/home-wage/process/init', data);
  },
  
  // 단계 확정
  confirmStep: (data: { mCode: number | string; bizGubun?: string; yearMonth: string; step: number }) => {
    return api.post('/home-wage/process/confirm-step', data);
  },
  
  // 단계 해제
  releaseStep: (data: { mCode: number | string; bizGubun?: string; yearMonth: string; step: number }) => {
    return api.post('/home-wage/process/release-step', data);
  },
  
  // 단계 해제 가능 여부 확인
  canReleaseStep: (params: { mCode: number | string; bizGubun?: string; yearMonth: string; step: number }) => {
    return api.get('/home-wage/process/can-release', { params });
  },
  
  // ========================================
  // 사원정보 관련
  // ========================================
  
  // 사원 목록 조회 (caion_ltcs_emp_info + sudong)
  getMembers: (mCode: number | string) => {
    return api.get(`/home-wage/members/${mCode}`);
  },
  
  // 사원정보는 caion_ltcs_emp_info + sudong 사용
  // 별도 업로드 불필요 - employeeApi 사용
  
  // 대표자 설정
  setBoss: (data: { mCode: number | string; memberCode: number }) => {
    return api.post('/home-wage/members/set-boss', data);
  },
  
  // ========================================
  // 청구내역 관련
  // ========================================
  
  // 청구내역 조회
  getBillingData: (mCode: number | string, yearMonth: string, bizGubun?: string) => {
    return api.get(`/home-wage/billing/${mCode}`, { params: { yearMonth, bizGubun } });
  },
  
  // 청구내역 업로드
  uploadBillingData: (data: {
    mCode: number | string;
    yearMonth: string;
    billingData: Array<{
      date?: string;
      patientName?: string;
      patientCode?: string;
      caregiverName?: string;
      caregiverBirth?: string;
      caregiverCode?: string;
      caregiverGubun?: string;
      familyRel?: string;
      familyWho?: string;
      startTime?: string;
      endTime?: string;
      serviceGubun?: string;
      sugaCode?: string;
      sugaName?: string;
    }>;
    billingDetailData: Array<{
      payStatus?: string;
      patientName?: string;
      patientCode?: string;
      date?: string;
      startTime?: string;
      endTime?: string;
      caregiverName?: string;
      sugaCode?: string;
      sugaName?: string;
      sugaMoney?: number;
      sugaHours?: number;
      reductionScore?: number;
      reductionRate?: number;
    }>;
  }) => {
    return api.post('/home-wage/billing/upload', data);
  },
  
  // LTCS 청구내역 조회 (롱텀케어 데이터)
  getLtcsBillingData: (mCode: number | string, yearMonth: string, bizGubun?: string) => {
    return api.get(`/home-wage/billing/ltcs/${mCode}`, { params: { yearMonth, bizGubun } });
  },
  
  // LTCS 청구내역 동기화 (전체 교체)
  syncLtcsBillingData: (data: {
    mCode: number | string;
    yearMonth: string;
    bizGubun?: string;
    syncType?: 'history' | 'detail' | 'both';
  }) => {
    return api.post('/home-wage/billing/sync', data);
  },
  
  // LTCS 청구내역/청구상세 데이터 요청 (매크로 큐 등록)
  requestBillingCrawl: (data: {
    mCode: number | string;
    yearMonth: string;
    bizGubun?: string;
    requestType: 'history' | 'detail';
  }) => {
    return api.post('/home-wage/billing/request-crawl', data);
  },
  
  // LTCS 요청 상태 조회 (폴링용)
  getBillingRequestStatus: (cmqIdx: number) => {
    return api.get(`/home-wage/billing/request-status/${cmqIdx}`);
  },
  
  // LTCS 통합 데이터 요청 (청구내역+청구상세+요양보호사+수급자)
  requestAllBillingCrawl: (data: {
    mCode: number | string;
    yearMonth: string;
    bizGubun?: string;
  }) => {
    return api.post('/home-wage/billing/request-all', data);
  },
  
  // LTCS 통합 요청 상태 조회 (폴링용)
  getAllBillingRequestStatus: (cmqIds: number[]) => {
    return api.get('/home-wage/billing/request-status-all', { params: { cmqIds: cmqIds.join(',') } });
  },
  
  // LTCS 통합 동기화 실행 (청구내역+청구상세+종사자+수급자)
  syncAllLtcsData: (data: {
    mCode: number | string;
    yearMonth: string;
    bizGubun?: string;
  }) => {
    return api.post('/home-wage/billing/sync-all', data);
  },
  
  // LTCS 개별 동기화 실행 (타입별)
  syncIndividualLtcsData: (data: {
    mCode: number | string;
    yearMonth: string;
    bizGubun?: string;
    syncType: string; // 'billing' | 'detail' | 'employee' | 'beneficiary'
  }) => {
    return api.post('/home-wage/billing/sync-individual', data);
  },
  
  // ========================================
  // 지급일 관련
  // ========================================
  
  // 지급일 조회
  getPaymentDate: (params: { mCode: number | string; yearMonth: string; bizGubun?: string }) => {
    return api.get('/home-wage/payment-date', { params });
  },
  
  // 지급일 저장
  savePaymentDate: (data: { 
    mCode: number | string; 
    yearMonth: string; 
    paymentDate: string; 
    sanRate?: number;
    bizGubun?: string;
  }) => {
    return api.post('/home-wage/payment-date', data);
  },

  /** 임금설정 사원목록 (요양보호사는 청구내역 이름+생년월일 매칭 시만) */
  getWageSettingEmployees: (params: {
    mCode: number | string;
    yearMonth: string;
    bizGubun?: string;
    limit?: number;
  }) => {
    return api.get('/home-wage/employees', { params });
  },

  /** 요양보호사별 LTCS 청구내역·청구상세 */
  /** 연도별 공휴일 (caion_home_holiday) */
  getHolidays: (params: { year: number | string }) => {
    return api.get('/home-wage/holidays', { params });
  },

  getCaregiverClaims: (params: {
    mCode: number | string;
    yearMonth: string;
    caregiverName: string;
    caregiverBirth?: string | null;
    bizGubun?: string;
  }) => {
    return api.get('/home-wage/caregiver-claims', { params });
  },

  /** 임금설정 목록 — 당월 요양보호사 청구 일괄 */
  getCaregiverClaimsMonth: (params: {
    mCode: number | string;
    yearMonth: string;
    bizGubun?: string;
  }) => {
    return api.get('/home-wage/caregiver-claims-month', { params });
  },
  
  // ========================================
  // 급여 데이터 관련
  // ========================================
  
  // 요양보호사별 급여 데이터 조회
  getWageData: (mCode: number | string, yearMonth: string) => {
    return api.get(`/home-wage/wage-data/${mCode}`, { params: { yearMonth } });
  },
  
  // 요양보호사별 급여 데이터 저장
  /** 임금명세서 PDF (전체·개별) */
  getWageSlipPdf: (params: {
    mCode: number | string;
    yearMonth: string;
    bizGubun?: string;
    memberName?: string;
    empKey?: string;
    disposition?: 'inline' | 'attachment';
  }) => {
    return api.get('/home-wage/wage-slip-pdf', { params, responseType: 'blob' });
  },

  /** 임금명세서 PDF ZIP (사원별) */
  getWageSlipPdfZip: (params: {
    mCode: number | string;
    yearMonth: string;
    bizGubun?: string;
    memberName?: string;
  }) => {
    return api.get('/home-wage/wage-slip-pdf-zip', { params, responseType: 'blob' });
  },

  saveWageData: (data: {
    mCode: number | string;
    yearMonth: string;
    data: {
      ceIdx?: number;
      caregiverName: string;
      caregiverBirth?: string;
      totalServiceDays?: number;
      totalServiceHours?: number;
      totalSugaMoney?: number;
      timePay?: number;
      monthlyPay?: number;
      overtimePay?: number;
      holidayPay?: number;
      laborDayPay?: number;
      lunchPay?: number;
      severeCareAddition?: number;
      longServicePay?: number;
      totalWage?: number;
      lYeon?: number;
      lGun?: number;
      lJang?: number;
      lGo?: number;
      lSo?: number;
      lJi?: number;
      lTotal?: number;
      cYeon?: number;
      cGun?: number;
      cJang?: number;
      cGo?: number;
      cSil?: number;
      cSan?: number;
      cOut?: number;
      cTotal?: number;
      netWage?: number;
      manualFlags?: Record<string, boolean>;
      memo?: string;
    };
  }) => {
    return api.post('/home-wage/wage-data/save', data);
  }
};

// ========================================
// 공동인증서 관리 API
// ========================================
export const certApi = {
  // 인증서 업로드
  upload: (formData: FormData) => {
    return api.post('/cert/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // 인증서 정보 조회
  getInfo: (mCode: number | string) => {
    return api.get(`/cert/${mCode}`);
  },

  // 인증서 목록 조회
  getList: (mCode: number | string) => {
    return api.get(`/cert/list/${mCode}`);
  },

  // 인증서 존재 여부 확인
  checkExists: (mCode: number | string) => {
    return api.get(`/cert/check/${mCode}`);
  },

  // 인증서 삭제
  delete: (cicIdx: number | string) => {
    return api.delete(`/cert/${cicIdx}`);
  },

  // 인증서 비밀번호 변경
  updatePassword: (cicIdx: number | string, password: string) => {
    return api.put(`/cert/${cicIdx}/password`, { password });
  },

  // ---- 관리자용 API ----

  // 전체 인증서 목록 조회 (관리자용)
  getAllCerts: () => {
    return api.get('/cert/admin/list');
  },

  // 인증서별 허용 IP 목록 조회
  getAllowedIPs: (cicIdx: number | string) => {
    return api.get(`/cert/admin/${cicIdx}/ips`);
  },

  // 허용 IP 추가
  addAllowedIP: (cicIdx: number | string, data: { ip: string; description?: string }) => {
    return api.post(`/cert/admin/${cicIdx}/ips`, data);
  },

  // 허용 IP 삭제
  deleteAllowedIP: (ccaiIdx: number | string) => {
    return api.delete(`/cert/admin/ips/${ccaiIdx}`);
  },

  // 허용 IP 활성화/비활성화 토글
  toggleAllowedIP: (ccaiIdx: number | string, isActive: boolean) => {
    return api.patch(`/cert/admin/ips/${ccaiIdx}/toggle`, { isActive });
  },

  // 현재 접속자 IP 조회
  getMyIP: () => {
    return api.get('/cert/admin/my-ip');
  },

  // 최고관리자 — 인증서 비밀정보 조회 (평문)
  adminGetSecrets: (mCode: number | string) => {
    return api.get(`/cert/admin/${mCode}/secrets`);
  },

  // 최고관리자 — 복호화된 인증서 ZIP 다운로드
  adminDownloadZip: async (mCode: number | string, fallbackName?: string) => {
    const response = await api.get(`/cert/admin/${mCode}/download`, {
      responseType: 'blob',
    });
    downloadAxiosBlobResponse(
      response,
      fallbackName || `ltcs-cert_${mCode}.zip`,
      'application/zip',
    );
  },
};

// ========================================
// 희망이음 전용 공동인증서 API
// (개인 공동인증서 + 희망이음 로그인 id/pw)
// ========================================
export const hopeCertApi = {
  // 인증서 업로드 (signCert.der + signPri.key + 희망이음 id/pw)
  upload: (formData: FormData) => {
    return api.post('/hope-cert/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // 인증서 정보 조회
  getInfo: (mCode: number | string) => {
    return api.get(`/hope-cert/${mCode}`);
  },

  // 인증서 목록 조회
  getList: (mCode: number | string) => {
    return api.get(`/hope-cert/list/${mCode}`);
  },

  // 인증서 존재 여부 확인
  checkExists: (mCode: number | string) => {
    return api.get(`/hope-cert/check/${mCode}`);
  },

  // 인증서 삭제
  delete: (chcIdx: number | string) => {
    return api.delete(`/hope-cert/${chcIdx}`);
  },

  // 인증서 비밀번호 변경
  updatePassword: (chcIdx: number | string, password: string) => {
    return api.put(`/hope-cert/${chcIdx}/password`, { password });
  },

  // 희망이음 로그인정보(id/pw) 변경
  updateCredentials: (chcIdx: number | string, hopeId: string, hopePw: string) => {
    return api.put(`/hope-cert/${chcIdx}/credentials`, { hopeId, hopePw });
  },

  // 희망이음 로그인정보(id/pw) 조회 — 수정 모달용
  getCredentials: (mCode: number | string) => {
    return api.get(`/hope-cert/${mCode}/credentials`);
  },

  // 전체 희망이음 인증서 목록 (최고관리자용)
  getAllCerts: () => {
    return api.get('/hope-cert/admin/list');
  },

  // 최고관리자 — 인증서 비밀정보 조회 (평문)
  adminGetSecrets: (mCode: number | string) => {
    return api.get(`/hope-cert/admin/${mCode}/secrets`);
  },

  // 최고관리자 — 복호화된 인증서 ZIP 다운로드
  adminDownloadZip: async (mCode: number | string, fallbackName?: string) => {
    const response = await api.get(`/hope-cert/admin/${mCode}/download`, {
      responseType: 'blob',
    });
    downloadAxiosBlobResponse(
      response,
      fallbackName || `hope-cert_${mCode}.zip`,
      'application/zip',
    );
  },
};

// 롱텀케어 자료요청 관련 API
export const ltcsApi = {
  /** 인건비지출내역 조회 */
  getLaborCost: (mCode: number | string, yearMonth: string, bizGubun?: string) => {
    return api.get(`/ltcs/labor-cost/${mCode}/${yearMonth}`, { params: { bizGubun } });
  },
};

// 방문요양/목욕 일정계획 관련 API
export const homeScheduleApi = {
  // ========================================
  // 수급자 관련
  // ========================================
  
  // 수급자 목록 조회 (전체 기간)
  getBeneficiaries: (mCode: number | string, bizGubun?: string) => {
    return api.get(`/home-schedule/beneficiaries/${mCode}`, { params: { bizGubun } });
  },

  // 수급자 목록 조회 (특정 연월)
  getBeneficiariesByMonth: (mCode: number | string, yearMonth: string, bizGubun?: string) => {
    return api.get(`/home-schedule/beneficiaries/${mCode}/${yearMonth}`, { params: { bizGubun } });
  },

  // 수급자 월별 요약 조회
  getBeneficiaryMonthlySummary: (mCode: number | string, patientCode: string, bizGubun?: string) => {
    return api.get(`/home-schedule/beneficiary/monthly/${mCode}/${encodeURIComponent(patientCode)}`, { params: { bizGubun } });
  },

  // 수급자 일정 상세 조회
  getBeneficiarySchedule: (mCode: number | string, patientCode: string, yearMonth: string, bizGubun?: string) => {
    return api.get(`/home-schedule/beneficiary/schedule/${mCode}/${encodeURIComponent(patientCode)}/${yearMonth}`, { params: { bizGubun } });
  },

  // 수급자 전체 일정 상세 조회 (해당 연월)
  getAllBeneficiarySchedules: (mCode: number | string, yearMonth: string, bizGubun?: string) => {
    return api.get(`/home-schedule/beneficiary/schedule-all/${mCode}/${yearMonth}`, { params: { bizGubun } });
  },

  // ========================================
  // 종사자 관련
  // ========================================

  // 종사자 목록 조회 (전체 기간)
  getCaregivers: (mCode: number | string, bizGubun?: string) => {
    return api.get(`/home-schedule/caregivers/${mCode}`, { params: { bizGubun } });
  },

  // 종사자 목록 조회 (특정 연월)
  getCaregiversByMonth: (mCode: number | string, yearMonth: string, bizGubun?: string) => {
    return api.get(`/home-schedule/caregivers/${mCode}/${yearMonth}`, { params: { bizGubun } });
  },

  // 종사자 일정 상세 조회
  getCaregiverSchedule: (mCode: number | string, empName: string, yearMonth: string, bizGubun?: string, empBirth?: string) => {
    return api.get(`/home-schedule/caregiver/schedule/${mCode}/${encodeURIComponent(empName)}/${yearMonth}`, { params: { bizGubun, empBirth } });
  },

  // 종사자 월별 요약 조회
  getCaregiverMonthlySummary: (mCode: number | string, empName: string, bizGubun?: string, empBirth?: string) => {
    return api.get(`/home-schedule/caregiver/monthly/${mCode}/${encodeURIComponent(empName)}`, { params: { bizGubun, empBirth } });
  },

  // ========================================
  // 통계
  // ========================================

  // 일정계획 통계 요약 조회
  getSummary: (mCode: number | string, yearMonth: string, bizGubun?: string) => {
    return api.get(`/home-schedule/summary/${mCode}/${yearMonth}`, { params: { bizGubun } });
  },

  // ========================================
  // PDF 다운로드
  // ========================================

  // 수급자 일정표 PDF 다운로드
  downloadBeneficiaryPdf: (mCode: number | string, patientCode: string, yearMonth: string, bizGubun?: string) => {
    return api.get(`/home-schedule/pdf/beneficiary/${mCode}/${encodeURIComponent(patientCode)}/${yearMonth}`, { 
      params: { bizGubun },
      responseType: 'blob' 
    });
  },

  // 종사자 일정표 PDF 다운로드
  downloadCaregiverPdf: (mCode: number | string, empName: string, yearMonth: string, bizGubun?: string, empBirth?: string) => {
    return api.get(`/home-schedule/pdf/caregiver/${mCode}/${encodeURIComponent(empName)}/${yearMonth}`, { 
      params: { bizGubun, empBirth },
      responseType: 'blob' 
    });
  },

  // 전체 수급자 일정표 PDF 다운로드
  downloadAllBeneficiariesPdf: (mCode: number | string, yearMonth: string, bizGubun?: string) => {
    return api.get(`/home-schedule/pdf/beneficiaries/${mCode}/${yearMonth}`, { 
      params: { bizGubun },
      responseType: 'blob' 
    });
  },

  // 전체 종사자 일정표 PDF 다운로드
  downloadAllCaregiversPdf: (mCode: number | string, yearMonth: string, bizGubun?: string) => {
    return api.get(`/home-schedule/pdf/caregivers/${mCode}/${yearMonth}`, { 
      params: { bizGubun },
      responseType: 'blob' 
    });
  },

  // ========================================
  // 급여대장 (caion_wage_home_director)
  // ========================================

  // 급여대장 목록 조회
  getWageData: (mCode: string, yearMonth: string, bizGubun?: string) => {
    return api.get(`/home-schedule/wage-data/${mCode}/${yearMonth}`, { params: { bizGubun } });
  },

  getDirectorWageData: (mCode: string, yearMonth: string, bizGubun?: string) => {
    return api.get(`/home-schedule/director-wage-data/${mCode}/${yearMonth}`, {
      params: { bizGubun },
    });
  },

  saveDirectorWageData: (data: Record<string, unknown>) => {
    return api.post('/home-schedule/director-wage-data', data);
  },

  saveDirectorWageDataBulk: (items: Record<string, unknown>[]) => {
    return api.post('/home-schedule/director-wage-data/bulk', { items });
  },

  // 급여대장 저장 (단건)
  saveWageData: (data: Record<string, unknown>) => {
    return api.post('/home-schedule/wage-data', data);
  },

  // 급여대장 일괄 저장
  saveWageDataBulk: (items: Record<string, unknown>[]) => {
    return api.post('/home-schedule/wage-data/bulk', { items });
  },

  // 급여대장 삭제
  deleteWageData: (idx: number) => {
    return api.delete(`/home-schedule/wage-data/${idx}`);
  },

  /** 해당 연월 임금 데이터 전체 삭제 (관리자, 급여대장 soft + 수급자설정 hard) */
  deleteMonthWageData: (params: {
    mCode: string;
    yearMonth: string;
    bizGubun?: string;
  }) => {
    const { mCode, yearMonth, bizGubun = '' } = params;
    const ym = String(yearMonth).replace(/-/g, '').substring(0, 6);
    return api.delete(`/home-schedule/wage-data/month/${mCode}/${ym}`, {
      params: { bizGubun },
    });
  },

  /** 사원별 임금 데이터 삭제 (관리자, 급여대장 soft + 수급자설정 hard) */
  deleteEmployeeWageData: (params: {
    mCode: string;
    yearMonth: string;
    bizGubun?: string;
    name: string;
    birth?: string | null;
  }) => {
    const { mCode, yearMonth, ...query } = params;
    return api.delete(`/home-schedule/wage-data/employee/${mCode}/${yearMonth}`, { params: query });
  },

  // ========================================
  // 수급자별 시급/수가시간 설정 (caion_wage_home_recipient_setting)
  // ========================================

  // 수급자별 설정 조회
  getRecipientSettings: (params: {
    mCode: string;
    yearMonth: string;
    bizGubun?: string;
    empName: string;
    empBirth?: string | null;
  }) => {
    const { mCode, yearMonth, ...query } = params;
    return api.get(`/home-schedule/recipient-settings/${mCode}/${yearMonth}`, { params: query });
  },

  /** 임금설정 목록 — 당월 수급자 시급 일괄 */
  getRecipientSettingsMonth: (params: {
    mCode: string;
    yearMonth: string;
    bizGubun?: string;
  }) => {
    const { mCode, yearMonth, ...query } = params;
    return api.get(`/home-schedule/recipient-settings/${mCode}/${yearMonth}/bulk`, { params: query });
  },

  // 수급자별 설정 저장
  saveRecipientSettings: (data: {
    m_code: string;
    yearMonth: string;
    bizGubun?: string;
    empName: string;
    empBirth?: string | null;
    hourlyByGroup?: Record<string, number>;
    severeFeeByGroup?: Record<string, number>;
    sugaHoursByGroup?: Record<string, number>;
  }) => {
    return api.post('/home-schedule/recipient-settings', data);
  },

  // 이전 달 4대보험 고정값 조회
  getPrevMonthInsurance: (params: {
    mCode: string;
    yearMonth: string;
    bizGubun?: string;
    name: string;
    birth?: string | null;
  }) => {
    const { mCode, yearMonth, ...query } = params;
    return api.get(`/home-schedule/wage-data/${mCode}/${yearMonth}/prev-insurance`, { params: query });
  },

  /** 과거 수급자별 시급·수가시간 (당월 이전 groupKey별 최신) */
  getHistoricalRecipientHourly: (params: {
    mCode: string;
    yearMonth: string;
    bizGubun?: string;
    empName: string;
    empBirth?: string | null;
    groupKeys: string[];
  }) => {
    const { mCode, yearMonth, groupKeys, ...query } = params;
    return api.get(`/home-schedule/recipient-settings/${mCode}/${yearMonth}/historical-hourly`, {
      params: {
        ...query,
        groupKeys: groupKeys.join(','),
      },
    });
  },

  /** 임금 엑셀 마이그레이션 (관리자) */
  downloadMigrationTemplate: () => {
    return api.get('/home-schedule/wage-migration/template', { responseType: 'blob' });
  },

  validateMigrationExcel: (formData: FormData) => {
    return api.post('/home-schedule/wage-migration/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  importMigration: (data: {
    mCode: string;
    yearMonth: string;
    bizGubun?: string;
    payDate?: string | null;
    rows: unknown[];
    registerMissingEmployees?: boolean;
  }) => {
    return api.post('/home-schedule/wage-migration/import', data);
  },
};

// ========================================
// 공휴일 관리 API (관리자)
// ========================================
export const dashMemoApi = {
  listMonth: (year: number, month: number) =>
    api.get('/admin/dash-memos', { params: { year, month } }),
  save: (date: string, body: string) =>
    api.put('/admin/dash-memos', { date, body }),
};

export const holidayManageApi = {
  listByYear: (year: number | string) => {
    return api.get('/admin/holidays', { params: { year } });
  },

  listByMonth: (year: number | string, month: number | string) => {
    return api.get('/admin/holidays/month', { params: { year, month } });
  },

  getFixed: () => {
    return api.get('/admin/holidays/fixed');
  },

  ensureYear: (year: number | string) => {
    return api.post('/admin/holidays/ensure-year', { year });
  },

  upsert: (data: { date: string; name: string }) => {
    return api.post('/admin/holidays', data);
  },

  update: (idx: number | string, data: { date?: string; name?: string }) => {
    return api.put(`/admin/holidays/${idx}`, data);
  },

  remove: (idx: number | string) => {
    return api.delete(`/admin/holidays/${idx}`);
  },

  removeByDate: (date: string) => {
    return api.delete(`/admin/holidays/by-date/${date}`);
  },
};

// ========================================
// 홍보메일 수신목록 (회원 제외 엑셀 · 발송 없음)
// ========================================
export const promoMailListApi = {
  stats: () => api.get('/admin/promo-mail-list/stats'),

  caionEmails: () => api.get('/admin/promo-mail-list/caion-emails'),

  matchRegions: (groups: {
    sentOk: string[];
    sentFail: string[];
    opened: string[];
    clicked: string[];
    unsub: string[];
  }) => api.post('/admin/promo-mail-list/regions', groups),

  matchEffects: (body: {
    campaigns: Array<{
      name: string;
      sent: Array<{ email: string; at: string }>;
      opens: Array<{ email: string; at: string }>;
      clicks: Array<{ email: string; at: string }>;
      mailProducts: string[];
    }>;
  }) => api.post('/admin/promo-mail-list/effects', body),

  saveCampaign: (body: Record<string, unknown>) =>
    api.post('/admin/promo-mail-list/campaigns', body),

  analyzeCampaigns: (names?: string[]) =>
    api.get('/admin/promo-mail-list/campaigns', {
      params: names?.length ? { names: names.join('\n') } : undefined,
    }),

  deleteCampaigns: (names: string[]) =>
    api.post('/admin/promo-mail-list/campaigns/delete', { names }),

  createGroup: (name: string) =>
    api.post('/admin/promo-mail-list/campaigns/groups', { name }),

  renameGroup: (idx: number, name: string) =>
    api.post('/admin/promo-mail-list/campaigns/groups/rename', { idx, name }),

  deleteGroup: (idx: number) =>
    api.post('/admin/promo-mail-list/campaigns/groups/delete', { idx }),

  moveCampaigns: (names: string[], groupIdx: number | null) =>
    api.post('/admin/promo-mail-list/campaigns/move', { names, groupIdx }),

  downloadExcel: async () => {
    const response = await api.get('/admin/promo-mail-list/excel', {
      responseType: 'blob',
    });
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    downloadAxiosBlobResponse(response, `홍보메일수신목록_회원제외_${stamp}.xlsx`);
    return response;
  },
};

/** 홍보 랜딩 HTML (장기요양지원센터.kr /a /b /c) */
export const promoHtmlApi = {
  listSlots: () => api.get('/admin/promo-html/slots'),

  landingStats: (slot: string) => api.get(`/admin/promo-html/slots/${slot}/stats`),

  deleteClicker: (slot: string, visitor: string) =>
    api.delete(`/admin/promo-html/slots/${slot}/clickers/${visitor}`),

  listVersions: (slot: string) => api.get(`/admin/promo-html/slots/${slot}/versions`),

  upload: (slot: string, file: File, title?: string) => {
    const formData = new FormData();
    formData.append('html', file);
    if (title && title.trim()) formData.append('title', title.trim());
    return api.post(`/admin/promo-html/slots/${slot}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  activate: (slot: string, idx: number | string) =>
    api.post(`/admin/promo-html/slots/${slot}/versions/${idx}/activate`),

  remove: (slot: string, idx: number | string) =>
    api.delete(`/admin/promo-html/slots/${slot}/versions/${idx}`),

  download: async (slot: string, idx: number | string, fallbackName: string) => {
    const name = fallbackName && /\.html?$/i.test(fallbackName)
      ? fallbackName
      : `${fallbackName || `${slot}-${idx}`}.html`;
    const response = await api.get(`/admin/promo-html/slots/${slot}/versions/${idx}/download`, {
      responseType: 'blob',
      timeout: 60000,
    });
    // HTML을 엑셀 MIME으로 감싸면 브라우저가 문서를 열거나 확장자를 바꿔 받는다.
    downloadAxiosBlobResponse(response, name, 'application/octet-stream');
    return response;
  },
};

export const ltcsScheduleProbeApi = {
  searchMembers: (search: string) =>
    api.get('/admin/ltcs-schedule-probe/members', { params: { search, limit: 30 } }),
  listRecipients: (mCode: string) =>
    api.get(`/admin/ltcs-schedule-probe/members/${encodeURIComponent(mCode)}/recipients`),
  listSchedules: (mCode: string, yearMonth: string) =>
    api.get(`/admin/ltcs-schedule-probe/members/${encodeURIComponent(mCode)}/schedules`, {
      params: { yearMonth },
    }),
  startTest: (body: {
    mCode: string
    afterDate: string
    afterStart: string
    afterEnd: string
    clspIdx: number
  }) => api.post('/admin/ltcs-schedule-probe/test', body),
  startServerTest: (body: {
    mCode: string
    afterDate: string
    afterStart: string
    afterEnd: string
    clspIdx: number
  }) => api.post('/admin/ltcs-schedule-probe/server-test', body),
  getCurrent: () => api.get('/admin/ltcs-schedule-probe/current'),
  getScreen: () => api.get('/admin/ltcs-schedule-probe/screen', { responseType: 'blob' }),
  getJob: (cmqIdx: number) => api.get(`/admin/ltcs-schedule-probe/jobs/${cmqIdx}`),
  stopJob: (cmqIdx: number) => api.post(`/admin/ltcs-schedule-probe/jobs/${cmqIdx}/stop`),
};

/** 직종 마스터 — 슈퍼바이저(100) CRUD */
export const jobTypeAdminApi = {
  list: (includeInactive = true) =>
    api.get('/admin/job-types', { params: { includeInactive: includeInactive ? '1' : '0' } }),
  create: (body: { name: string; sort?: number; isProduction?: boolean }) =>
    api.post('/admin/job-types', body),
  update: (
    idx: number,
    body: { name?: string; sort?: number; isProduction?: boolean; status?: 'Y' | 'N' }
  ) => api.put(`/admin/job-types/${idx}`, body),
  remove: (idx: number) => api.delete(`/admin/job-types/${idx}`),
};


// ========================================
// 공지사항 (관리자)
// ========================================
export const noticeAdminApi = {
  list: (view: 'board' | 'manage' = 'manage') =>
    api.get('/admin/notices', { params: { view } }),
  detail: (idx: number) => api.get(`/admin/notices/${idx}`),
  create: (body: { title: string; body: string; pinned: boolean; status: 'Y' | 'N' }) =>
    api.post('/admin/notices', body),
  update: (idx: number, body: { title: string; body: string; pinned: boolean; status: 'Y' | 'N' }) =>
    api.put(`/admin/notices/${idx}`, body),
  remove: (idx: number) => api.delete(`/admin/notices/${idx}`),
};

export const boardImageApi = {
  upload: (file: Blob) => {
    const form = new FormData();
    form.append('file', file, 'paste.png');
    return api.post('/admin/board-images', form, {
      timeout: 20000,
      transformRequest: [(data, headers) => {
        if (headers) delete headers['Content-Type'];
        return data;
      }],
    });
  },
  fetch: (file: string) => api.get(`/admin/board-images/${file}`, { responseType: 'blob' }),
};

export const ltcsAdminEnterApi = {
  openUrl: () => api.post('/admin/ltcs-admin/enter'),
};

export const boardApi = {
  list: (params: { page?: number; limit?: number; keyword?: string; field?: 'title' | 'author'; kind?: 'A' | 'N' | 'Q' | 'P' }) =>
    api.get('/admin/board', { params }),
  notices: () => api.get('/admin/board', { params: { scope: 'notice' } }),
  detail: (idx: number, params?: { peek?: number; keyword?: string; field?: 'title' | 'author'; nav?: 'A' | 'N' | 'Q' | 'P' }) =>
    api.get(`/admin/board/${idx}`, { params }),
  create: (body: {
    kind: 'N' | 'Q' | 'P';
    title: string;
    body: string;
    secret?: boolean;
    pinned?: boolean;
    status?: 'Y' | 'N';
  }) => api.post('/admin/board', body),
  update: (idx: number, body: {
    title: string;
    body: string;
    secret?: boolean;
    pinned?: boolean;
    status?: 'Y' | 'N';
  }) => api.put(`/admin/board/${idx}`, body),
  reply: (idx: number, reply: string) => api.put(`/admin/board/${idx}/reply`, { reply }),
  remove: (idx: number) => api.delete(`/admin/board/${idx}`),
  comment: (idx: number, body: string) => api.post(`/admin/board/${idx}/comments`, { body }),
  updateComment: (idx: number, commentIdx: number, body: string) =>
    api.put(`/admin/board/${idx}/comments/${commentIdx}`, { body }),
  removeComment: (idx: number, commentIdx: number) => api.delete(`/admin/board/${idx}/comments/${commentIdx}`),
};

export const staffInquiryApi = {
  list: () => api.get('/admin/inquiries'),
  detail: (idx: number) => api.get(`/admin/inquiries/${idx}`),
  create: (body: { title: string; body: string }) => api.post('/admin/inquiries', body),
  reply: (idx: number, reply: string) => api.put(`/admin/inquiries/${idx}/reply`, { reply }),
  remove: (idx: number) => api.delete(`/admin/inquiries/${idx}`),
};

// ========================================
// 상담요청 관리 API (관리자)
// ========================================
export const consultBoardApi = {
  listRequests: (params: {
    page?: number;
    limit?: number;
    keyword?: string;
    status?: string;
    kind?: string;
  }) => {
    return api.get('/consult-board/admin/requests', { params });
  },

  getRequestDetail: (id: number | string) => {
    return api.get(`/consult-board/admin/requests/${id}`);
  },

  createResponse: (id: number | string, content: string, excel?: File | null) => {
    const formData = new FormData()
    formData.append('content', content)
    if (excel) {
      formData.append('excel', excel)
      formData.append('excelOriginalName', excel.name)
    }
    return api.post(`/consult-board/admin/requests/${id}/responses`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  downloadExcel: async (id: number | string, fallbackName: string) => {
    await downloadConsultExcelBlob(
      `/consult-board/admin/requests/${id}/excel`,
      fallbackName,
    )
  },

  downloadResponseExcel: async (
    reqId: number | string,
    responseId: number | string,
    fallbackName: string,
  ) => {
    await downloadConsultExcelBlob(
      `/consult-board/admin/requests/${reqId}/responses/${responseId}/excel`,
      fallbackName,
    )
  },

  postNontaxCompare: (payload: {
    name: string;
    ym: string;
    rows: Array<Record<string, unknown>>;
  }) => {
    return api.post('/consult-board/admin/nontax-compare', payload);
  },

  suggestAiReplies: (id: number | string) => {
    return api.post(`/consult-board/admin/requests/${id}/ai-replies`);
  },

  deleteRequest: (id: number | string) => {
    return api.delete(`/consult-board/admin/requests/${id}`);
  },
};

async function downloadConsultExcelBlob(url: string, fallbackName: string) {
  const response = await api.get(url, { responseType: 'blob' })
  downloadAxiosBlobResponse(response, fallbackName)
}

export default api;
